import React, { useMemo, useState, useEffect, useCallback, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  CircularProgress,
  useTheme,
  Button,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchEntitiesIfNeeded, setSelectedSourceEntities, setSelectedDestinationEntities } from '../state/slices/entitiesSlice';
import { saveMappingThunk, setConnectionDetailsForWorkflow, getSavedMapping, clearFuzzyStateForWorkflow, setAutoFuzzyEnabled } from '../state/slices/mappingSlice';
import store from '../state/store';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { FilterSelect, EntityMultiSelect, GroupedFieldSelect } from '../components/ui/select';
import { PrimaryButton } from '../components/ui/button';
import { toast } from 'react-toastify';
import { fetchTypeBasedConnection, fetchJiraProjects, addProjectKeyToJiraConnection } from '../apis/Connections';
import { fetchModulesBySource } from '../apis/EntitiesSelection';
import { ThemeContext } from '../Context/ThemeContext';
import { functionList, groupedFunctions } from './Component/FunctionToolbar';
import { PrimaryDialog, DeleteConfirmDialog } from '../components/ui/dialog';
import ConfirmDialog from './Component/ConfirmDialog';
import { PrimaryTabs } from '../components/ui/tabs';
import ActionButtons from './ActionButtons';
import { getAllTypeAliases } from '../utils/formatters/stringFormatter';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';

const sanitizeEntityForAPI = (entity) => {
  if (!entity) return null;
  
  if (typeof entity === 'string') {
    return {
      entity_name: entity,
      label: entity,
    };
  }
  
  const entityName = entity.entity_name || entity.entity || entity.name || entity.value || entity.label || '';
  if (!entityName) return null;
  
  return {
    entity_name: entityName,
    label: entity.label || entity.name || entityName,
  };
};

// Function type compatibility - defines which input types each function accepts
const functionTypeCompatibility = {
  substring: ['string'],
  aggregation: ['string'],
  field_split: ['string'],
  trim: ['string'],
  remove_special_characters: ['string', 'character'],
  currency_format: ['number', 'int', 'integer', 'float', 'double'],
  format_date: ['string', 'date', 'datetime', 'timestamp'],
  add_date: ['string', 'date', 'datetime', 'timestamp'],
  order_by: ['any'],
  map: ['any'],
  concat: ['string'],
  uppercase: ['string'],
  lowercase: ['string'],
  length: ['string'],
  replace: ['string'],
  mean: ['number', 'integer', 'int'],
  median: ['number', 'integer', 'int'],
  mode: ['number', 'integer', 'int'],
  to_timestamp: ['date', 'timestamp', 'datetime', 'string'],
  extract: ['string', 'date', 'datetime', 'timestamp'],
  day_of_week: ['string', 'date', 'datetime', 'timestamp'],
  padLeftRight: ['string'],
  parse_int: ['string', 'double'],
  parse_double: ['integer', 'int', 'string'],
  convert_to_string: ['integer', 'number', 'int', 'string', 'bit'],
  join: ['string'],
  lookup: ['string'],
  relation: ['string'],
  registry: ['reference'],
};

// Function output types - defines what types each function outputs
const functionOutputTypes = {
  substring: ['string'],
  aggregation: ['any'],
  field_split: ['string'],
  trim: ['string'],
  remove_special_characters: ['string'],
  currency_format: ['string'],
  format_date: ['string'],
  add_date: ['date', 'datetime', 'timestamp'],
  order_by: ['any'],
  map: ['any'],
  concat: ['string'],
  uppercase: ['string'],
  lowercase: ['string'],
  length: ['number', 'integer', 'int'],
  replace: ['string'],
  mean: ['number', 'float', 'double'],
  median: ['number', 'float', 'double'],
  mode: ['number', 'float', 'double'],
  to_timestamp: ['timestamp', 'datetime'],
  extract: ['string', 'number', 'integer', 'int'],
  day_of_week: ['number', 'integer', 'int'],
  padLeftRight: ['string'],
  parse_int: ['integer', 'int'],
  parse_double: ['double', 'float'],
  convert_to_string: ['string'],
  join: ['string'],
  lookup: ['string'],
  relation: ['string'],
  registry: ['reference'],
};

const STEP_LABELS = [
  'Select Dataset',
  'Configure Dataset',
  'Entity Selection',
  'Mapping',
];

const BottomConfigurationPanel = ({
  currentStep = 'selectDataset',
  onStepChange,
  mappingData,
  resolvedFields,
  autoMappedFields,
  selectedNode,
  showMappingPanel = false,
  nodes = [],
  workflowId,
  onEntitySelectionComplete,
  onSwitchToVisualBuilder,
  actionRef,
  workflowConfigurationId,
  onWorkflowExecutionComplete,
  onSave,
  collapsedBoth,
  onCollapsedChange,
  leftSidebarRef,
  rightSidebarRef,
  updateNodeData,
  onNodeSelect,
}) => {
  const { workflowName: workflowIdFromParams } = useParams();
  const effectiveWorkflowId = workflowId || workflowIdFromParams;
  
  // Debug: Log all props when component renders or key props change
  useEffect(() => {
    console.log('=== BottomConfigurationPanel Debug ===');
    console.log('currentStep:', currentStep);
    console.log('resolvedFields:', resolvedFields);
    console.log('resolvedFields type:', typeof resolvedFields);
    console.log('resolvedFields isArray:', Array.isArray(resolvedFields));
    console.log('resolvedFields length:', resolvedFields?.length);
    if (resolvedFields && resolvedFields.length > 0) {
      console.log('resolvedFields first item:', resolvedFields[0]);
      console.log('resolvedFields structure:', JSON.stringify(resolvedFields[0], null, 2));
    }
    console.log('autoMappedFields:', autoMappedFields);
    console.log('mappingData:', mappingData);
    console.log('showMappingPanel:', showMappingPanel);
    console.log('=====================================');
  }, [resolvedFields, currentStep, autoMappedFields, mappingData, showMappingPanel]);
  
  const [collapsed, setCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bottomPanelTab, setBottomPanelTab] = useState(0); 
  // Commented: not used - using Redux only for consistency
  // const [selectedSourceEntity, setSelectedSourceEntity] = useState([]);
  // const [selectedDestinationEntity, setSelectedDestinationEntity] = useState([]);
  const [activeDestinationId, setActiveDestinationId] = useState(null);
  const [mappingRows, setMappingRows] = useState([
    { 
      id: 1, 
      sourceField: '', 
      functionType: '',      // For backward compatibility
      function: '',          // For backward compatibility (single function)
      destinationField: '', 
      functionConfig: {},    // For backward compatibility
      functionChain: []      // New: array of functions for multi-function chains
    }
  ]);
  // Use Redux for autoFuzzyEnabled to match etl-frontend behavior
  const autoFuzzyEnabled = useSelector(
    (state) => state.mapping.autoFuzzyEnabledByWorkflow?.[effectiveWorkflowId] ?? true,
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [savedMappings, setSavedMappings] = useState([]);
  const [editingMapping, setEditingMapping] = useState(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteMapping, setPendingDeleteMapping] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const configScrollRef = useRef(null);

  // Handle scroll detection for fade in/out scrollbar
  useEffect(() => {
    const scrollContainer = configScrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Hide scrollbar after 1 second of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [bottomPanelTab]);


  useEffect(() => {
    console.log('groupedFunctions keys:', Object.keys(groupedFunctions || {}));
    console.log('functionList length:', functionList?.length);
  }, []);

  const sourceNodes = useMemo(() => 
    nodes.filter((n) => n.type === 'sourceNode' || n.type === 'source'),
    [nodes]
  );
  const destNodes = useMemo(() => 
    nodes.filter((n) => n.type === 'destinationNode' || n.type === 'destination'),
    [nodes]
  );

  // Helper to build node key (same as EntitySelectionDialog)
  // Works for all connectors: uses actual module value from config
  // Only SQL connectors default to 'sql' if module is empty
  const buildNodeKey = useCallback((node) => {
    const nodeModule = node.data?.config?.module || '';
    const connId = node.data?.config?.connection?.conn_id || '';
    
    // Check if it's a SQL connector (by node ID or connector name)
    const isSqlConnector = node.id?.toLowerCase().startsWith('sql') || 
                          node.data?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                          node.data?.config?.connection?.connector?.toLowerCase() === 'sql';
    
    // Normalize module: for SQL, normalize to lowercase; for others, use as-is
    // Only default to 'sql' if it's a SQL connector and module is empty
    let normalizedModule = nodeModule;
    if (isSqlConnector) {
      if (!nodeModule) {
        normalizedModule = 'sql'; // Default for SQL when empty
      } else if (nodeModule.toLowerCase() === 'sql') {
        normalizedModule = 'sql'; // Normalize SQL to lowercase
      }
    }
    // For all other connectors, use the module value as-is (preserves case like "Salesforce Financial Services Cloud")
    
    return `${node.id}-${connId}-${normalizedModule}`;
  }, []);

  // Commented: not used - using per-node approach with Redux for all scenarios
  // const sourceNodeKey = useMemo(() => {
  //   if (sourceNodes.length === 0) return null;
  //   const node = sourceNodes[0];
  //   const connId = node.data?.config?.connection?.conn_id || '';
  //   const mod = node.data?.config?.module || '';
  //   return `${node.id}-${connId}-${mod}`;
  // }, [sourceNodes]);

  const destNodeKey = useMemo(() => {
    if (destNodes.length === 0) return null;
    const node = destNodes[0];
    return buildNodeKey(node);
  }, [destNodes, buildNodeKey]);

  const sourceEntitiesByWorkflow = useSelector(
    (state) => state.entities?.entitiesByWorkflow?.[effectiveWorkflowId] || {}
  );
  const destinationEntitiesByWorkflow = useSelector(
    (state) => state.entities?.entitiesByWorkflow?.[effectiveWorkflowId] || {}
  );
  
  // Redux state for per-node entity selections (for Many-to-One support)
  const selectedSourceEntitiesByWorkflow = useSelector(
    (state) => state.entities?.selectedSourceEntitiesByWorkflow?.[effectiveWorkflowId] || {}
  );
  const selectedDestinationEntitiesByWorkflow = useSelector(
    (state) => state.entities?.selectedDestinationEntitiesByWorkflow?.[effectiveWorkflowId] || {}
  );

  // Commented: not used - using per-node approach with Redux for all scenarios
  // const availableSourceEntities = useMemo(() => {
  //   if (!sourceNodeKey || !sourceEntitiesByWorkflow[sourceNodeKey]) return [];
  //   return sourceEntitiesByWorkflow[sourceNodeKey] || [];
  // }, [sourceNodeKey, sourceEntitiesByWorkflow]);

  // Get destination node key based on active destination (for many-to-one support)
  const activeDestNodeKey = useMemo(() => {
    if (!activeDestinationId || destNodes.length === 0) return destNodeKey;
    const activeDest = destNodes.find(d => d.id === activeDestinationId);
    if (!activeDest) return destNodeKey;
    return buildNodeKey(activeDest);
  }, [activeDestinationId, destNodes, destNodeKey, buildNodeKey]);

  const availableDestinationEntities = useMemo(() => {
    if (!activeDestNodeKey || !destinationEntitiesByWorkflow[activeDestNodeKey]) return [];
    return destinationEntitiesByWorkflow[activeDestNodeKey] || [];
  }, [activeDestNodeKey, destinationEntitiesByWorkflow]);

  const fetchedKeysRef = useRef(new Set());
  const lastProcessedKeysRef = useRef('');
  const dispatch = useDispatch();

  const buildPayload = useCallback((node) => {
    if (!node || !node.data?.config) return null;
    return {
      nodeId: node.id,
      label: node.data.config.label || node.data.label || null,
      config: {
        id: node.id,
        label: node.data.config.label || node.data.label || null,
        module: node.data.config.module || null,
        batches: node.data.config.batches || 1,
        connection: node.data.config.connection || null,
        sqlMetadata: node.data.config?.sqlMetadata || null,
      },
    };
  }, []);

  const sourceNodeKeys = useMemo(() => {
    return sourceNodes.map((node) => buildNodeKey(node)).sort().join(',');
  }, [sourceNodes]);

  const destNodeKeys = useMemo(() => {
    // For many-to-one: only use active destination, for one-to-one: use all
    if (destNodes.length > 1 && activeDestinationId) {
      const activeDest = destNodes.find(d => d.id === activeDestinationId);
      return activeDest ? buildNodeKey(activeDest) : '';
    }
    return destNodes.map((node) => buildNodeKey(node)).sort().join(',');
  }, [destNodes, activeDestinationId, buildNodeKey]);

  useEffect(() => {
    fetchedKeysRef.current.clear();
    lastProcessedKeysRef.current = '';
  }, [currentStep, effectiveWorkflowId]);

  // Initialize activeDestinationId from mappingData when coming from visual builder
  useEffect(() => {
    if (mappingData?.primaryDestinationId && destNodes.length > 1) {
      const primaryDestId = mappingData.primaryDestinationId;
      const destExists = destNodes.some(d => d.id === primaryDestId);
      if (destExists && activeDestinationId !== primaryDestId) {
        setActiveDestinationId(primaryDestId);
      }
    } else if (destNodes.length === 1 && activeDestinationId !== destNodes[0]?.id) {
      // For one-to-one, set to the single destination
      setActiveDestinationId(destNodes[0]?.id);
    } else if (destNodes.length > 0 && !activeDestinationId) {
      // Initialize to first destination if not set
      setActiveDestinationId(destNodes[0]?.id);
    }
  }, [mappingData?.primaryDestinationId, destNodes, activeDestinationId]);

  useEffect(() => {
    if (currentStep !== 'entitySelection' || !effectiveWorkflowId) return;
    if (sourceNodes.length === 0 || destNodes.length === 0) return;

    const currentKeys = `${sourceNodeKeys}|${destNodeKeys}`;
    
    if (lastProcessedKeysRef.current === currentKeys) {
      return;
    }
    
    lastProcessedKeysRef.current = currentKeys;

    sourceNodes.forEach((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      const hasConnection = sourceNode.data?.config?.connection?.conn_id;
      const cacheKey = `source-${effectiveWorkflowId}-${sourceKey}`;
      
      if (fetchedKeysRef.current.has(cacheKey)) {
        return;
      }
      
      const existingEntities = sourceEntitiesByWorkflow[sourceKey];
      // Check if entities exist and is a non-empty array
      const hasEntities = existingEntities && Array.isArray(existingEntities) && existingEntities.length > 0;
      
      if (hasConnection && !hasEntities) {
        fetchedKeysRef.current.add(cacheKey);
        
        // Build payload and ensure SQL metadata is included for SQL connectors
        const payload = buildPayload(sourceNode);
        if (payload && payload.config) {
          // Ensure connector_name is set for SQL detection
          if (!payload.config.connection?.connector_name && !payload.config.connection?.connector) {
            const nodeType = getNodeType(sourceNode);
            if (nodeType === 'sql') {
              payload.config.connection = {
                ...payload.config.connection,
                connector_name: 'sql',
                connector: 'sql',
              };
            }
          }
        }
        
        dispatch(
          fetchEntitiesIfNeeded({
            workflowId: effectiveWorkflowId,
            key: sourceKey,
            targetType: 'source',
            payload: {
              ...payload,
              workflowId: effectiveWorkflowId,
            },
          })
        );
      } else if (hasEntities) {
        // Mark as fetched if entities already exist
        fetchedKeysRef.current.add(cacheKey);
      }
    });

    // Fetch entities for destination nodes
    // For many-to-one: only fetch for active destination
    const destinationsToFetch = destNodes.length > 1 && activeDestinationId
      ? destNodes.filter(d => d.id === activeDestinationId)
      : destNodes;
    
    destinationsToFetch.forEach((destNode) => {
      const destKey = buildNodeKey(destNode);
      const hasConnection = destNode.data?.config?.connection?.conn_id;
      const cacheKey = `dest-${effectiveWorkflowId}-${destKey}`;
      
      if (fetchedKeysRef.current.has(cacheKey)) {
        return;
      }
      
      const existingEntities = destinationEntitiesByWorkflow[destKey];
      // Check if entities exist and is a non-empty array
      const hasEntities = existingEntities && Array.isArray(existingEntities) && existingEntities.length > 0;
      
      if (hasConnection && !hasEntities) {
        fetchedKeysRef.current.add(cacheKey);
        
        // Build payload and ensure SQL metadata is included for SQL connectors
        const payload = buildPayload(destNode);
        if (payload && payload.config) {
          // Ensure connector_name is set for SQL detection
          if (!payload.config.connection?.connector_name && !payload.config.connection?.connector) {
            const nodeType = getNodeType(destNode);
            if (nodeType === 'sql') {
              payload.config.connection = {
                ...payload.config.connection,
                connector_name: 'sql',
                connector: 'sql',
              };
            }
          }
        }
        
        dispatch(
          fetchEntitiesIfNeeded({
            workflowId: effectiveWorkflowId,
            key: destKey,
            targetType: 'destination',
            payload: {
              ...payload,
              workflowId: effectiveWorkflowId,
            },
          })
        );
      } else if (hasEntities) {
        // Mark as fetched if entities already exist
        fetchedKeysRef.current.add(cacheKey);
      }
    });
  }, [currentStep, effectiveWorkflowId, sourceNodeKeys, destNodeKeys, dispatch, buildNodeKey, buildPayload, activeDestinationId, destNodes, sourceNodes, destinationEntitiesByWorkflow, sourceEntitiesByWorkflow]);

  // Handle proceed to mapping
  const handleProceedToMapping = () => {
    console.log('=== handleProceedToMapping called ===');
    // Commented: using Redux only
    // console.log('selectedSourceEntity:', selectedSourceEntity);
    // console.log('selectedDestinationEntity:', selectedDestinationEntity);
    console.log('selectedSourceEntitiesByWorkflow:', selectedSourceEntitiesByWorkflow);
    console.log('selectedDestinationEntitiesByWorkflow:', selectedDestinationEntitiesByWorkflow);
    console.log('sourceNodes:', sourceNodes);
    console.log('destNodes:', destNodes);
    console.log('nodes:', nodes);
    
    // Validate: Check if all source nodes have entities selected (Many-to-One support)
    // Use Redux only (consistent approach)
    const allSourceNodesHaveEntities = sourceNodes.every((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      const entities = selectedSourceEntitiesByWorkflow[sourceKey] || [];
      return entities.length > 0;
    });
    
    // Validate: Check if destination has entities selected
    // Use Redux only (consistent approach)
    const targetDestIdForValidation = activeDestinationId || destNodes[0]?.id;
    const activeDest = destNodes.find(d => d.id === targetDestIdForValidation);
    const destKey = activeDest ? buildNodeKey(activeDest) : null;
    const destEntities = destKey ? (selectedDestinationEntitiesByWorkflow[destKey] || []) : [];
    const destHasEntities = destEntities.length > 0;
    
    if (!allSourceNodesHaveEntities || !destHasEntities) {
      toast.error('Please select entities for all source nodes and destination');
      return;
    }

    if (!onEntitySelectionComplete) {
      toast.error('Mapping handler not available');
      return;
    }

    if (sourceNodes.length === 0 || destNodes.length === 0) {
      toast.error('Source and destination nodes are required');
      return;
    }

    // Build finalConfigPayload similar to EntitySelectionDialog
    const allConfigs = nodes
      .map((node) => {
        const isSource = node.type === 'sourceNode' || node.type === 'source';
        const isDestination = node.type === 'destinationNode' || node.type === 'destination';
        
        if (!(isSource || isDestination)) return null;

        // Get selected entities for this node using Redux state (per-node tracking for Many-to-One)
        // For many-to-one: only process active destination
        const targetDestIdForNode = activeDestinationId || destNodes[0]?.id;
        if (isDestination && targetDestIdForNode && node.id !== targetDestIdForNode) {
          return null; // Skip non-active destinations in many-to-one scenario
        }
        
        // Use Redux state for per-node entity tracking (aligned with etl-frontend)
        const nodeKey = buildNodeKey(node);
        let selectedEntities = [];
        
        if (isSource) {
          // Get entities from Redux state (per-node tracking) - use Redux only
          const sourceEntities = selectedSourceEntitiesByWorkflow[nodeKey] || [];
          
          // Map selected entities to sanitized format
          selectedEntities = sourceEntities
            .map((selected) => {
              const entityName = typeof selected === 'string' ? selected : selected?.entity_name || selected?.name || selected?.value || selected?.label || '';
              // Find entity in available entities for this source node
              const availableEntities = sourceEntitiesByWorkflow[nodeKey] || [];
              const entityObj = availableEntities.find(
                (e) => {
                  const eName = typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '';
                  return eName === entityName;
                }
              );
              return sanitizeEntityForAPI(entityObj || selected);
            })
            .filter(Boolean);
        } else if (isDestination) {
          // Get entities from Redux state (per-node tracking) - use Redux only
          const destEntities = selectedDestinationEntitiesByWorkflow[nodeKey] || [];
          
          // Map selected entities to sanitized format
          selectedEntities = destEntities
            .map((selected) => {
              const entityName = typeof selected === 'string' ? selected : selected?.entity_name || selected?.name || selected?.value || selected?.label || '';
              // Find entity in available entities for this destination node
              const availableEntities = destinationEntitiesByWorkflow[nodeKey] || [];
              const entityObj = availableEntities.find(
                (e) => {
                  const eName = typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '';
                  return eName === entityName;
                }
              );
              return sanitizeEntityForAPI(entityObj || selected);
            })
            .filter(Boolean);
        }
        
        console.log('Node', node.id, 'selectedEntities:', selectedEntities);
        console.log('Node', node.id, 'nodeKey:', nodeKey);

        const config = {
          id: node.id,
          label: node.data?.config?.label || node.data?.label,
          connection: node.data?.config?.connection,
          module: node.data?.config?.module,
          sqlMetadata: node.data?.config?.sqlMetadata || null,
          selectedEntities,
          type: isSource ? 'source' : 'destination',
        };
        
        console.log('Built config for node:', node.id, config);
        return config;
      })
      .filter(Boolean);

    // Filter to only source and destination configs
    // For many-to-one: use activeDestinationId, for one-to-one: use first destination
    const targetDestId = activeDestinationId || destNodes[0]?.id;
    const filtered = allConfigs.filter((c) => {
      if (c.type === 'source') return true;
      if (c.type === 'destination') return c.id === targetDestId;
      return false;
    });

    console.log('Filtered configs:', filtered);

    const srcEntities = filtered
      .filter((c) => c.type === 'source')
      .flatMap((c) => c.selectedEntities || []);
    const dstEntities = filtered
      .filter((c) => c.type === 'destination')
      .flatMap((c) => c.selectedEntities || []);

    console.log('srcEntities:', srcEntities);
    console.log('dstEntities:', dstEntities);

    // Get entity names for display
    const sourceName = srcEntities[0] || '';
    const dest1Name = dstEntities[0] || '';

    // Build decisionMeta for many-to-one scenarios
    // Use mappingData.decisionMeta if available (from visual builder), otherwise build it
    let decisionMeta = null;
    if (destNodes.length > 1) {
      // Try to use decisionMeta from mappingData first (preserves decisionNodeId, sourceId from visual builder)
      if (mappingData?.decisionMeta && 
          Array.isArray(mappingData.decisionMeta.destIds) && 
          mappingData.decisionMeta.destIds.length > 0) {
        // Verify targetDestId is in the destIds array (nodes might have changed)
        const destIds = mappingData.decisionMeta.destIds;
        const targetIndex = destIds.indexOf(targetDestId);
        if (targetIndex >= 0) {
          // targetDestId is valid, reuse decisionMeta
          decisionMeta = {
            ...mappingData.decisionMeta,
            currentIndex: targetIndex, // Update currentIndex to match active destination
          };
        } else {
          // targetDestId not in destIds, rebuild decisionMeta (nodes may have changed)
          const newDestIds = destNodes.map(d => d.id);
          const newDestNames = destNodes.map(d => d?.data?.config?.label || d?.data?.label || '');
          const currentIndex = Math.max(0, newDestIds.indexOf(targetDestId));
          decisionMeta = {
            decisionNodeId: mappingData.decisionMeta.decisionNodeId || null,
            sourceId: mappingData.decisionMeta.sourceId || sourceNodes[0]?.id || null,
            destIds: newDestIds,
            destNames: newDestNames,
            currentIndex,
          };
        }
      } else {
        // Build new decisionMeta if not available from mappingData
        const destIds = destNodes.map(d => d.id);
        const destNames = destNodes.map(d => d?.data?.config?.label || d?.data?.label || '');
        const currentIndex = Math.max(0, destIds.indexOf(targetDestId));
        
        decisionMeta = {
          decisionNodeId: null, // Will be set if there's a decision node
          sourceId: sourceNodes[0]?.id || null,
          destIds,
          destNames,
          currentIndex,
        };
      }
    }

    // Calculate secondaryDestinationId correctly (next destination after current)
    let secondaryDestinationId = null;
    if (destNodes.length > 1 && decisionMeta) {
      const currentIndex = decisionMeta.currentIndex || 0;
      const destIds = decisionMeta.destIds || destNodes.map(d => d.id);
      secondaryDestinationId = destIds[currentIndex + 1] || null;
    }

    const payload = {
      finalConfigPayload: filtered,
      selectedSourceEntities: srcEntities,
      selectedDestinationEntities: dstEntities,
      canGoNext: destNodes.length > 1 && !!secondaryDestinationId, // Can go to next destination if many-to-one
      primaryDestinationId: targetDestId,
      secondaryDestinationId,
      decisionMeta,
      sourceName,
      dest1Name,
      shouldSkipFuzzy: !autoFuzzyEnabled,
    };
    
    console.log('Calling onEntitySelectionComplete with payload:', payload);
    console.log('finalConfigPayload structure:', JSON.stringify(filtered, null, 2));

    // Call the handler to trigger mapping flow
    onEntitySelectionComplete(payload);
  };

  // Get resolved fields for current entities
  // Structure: resolvedFields is an array of objects with:
  // - type: 'source' or 'destination'
  // - fields: object with nested fields array (for SQL) OR direct array (for non-SQL)
  //   - For SQL: fields.fields is the array
  //   - For non-SQL: fields might be the array directly or fields.fields
  const sourceFieldsGrouped = useMemo(() => {
    if (!resolvedFields || !Array.isArray(resolvedFields)) {
      return [];
    }
    
    // Use Redux state - get all selected entities from all source nodes
    const allSelectedSourceEntities = sourceNodes.flatMap((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      return selectedSourceEntitiesByWorkflow[sourceKey] || [];
    });
    
    if (!allSelectedSourceEntities || allSelectedSourceEntities.length === 0) {
      return [];
    }
    
    const sourceResolvedFields = resolvedFields.filter((f) => f.type === 'source');
    
    if (sourceResolvedFields.length === 0) {
      return [];
    }
    
    const grouped = [];
    
    allSelectedSourceEntities.forEach((selectedEntity, index) => {
      const entityLabel = typeof selectedEntity === 'string' ? selectedEntity : selectedEntity?.label || selectedEntity?.entity_name || selectedEntity?.value || '';
      const entityValue = typeof selectedEntity === 'string' ? selectedEntity : selectedEntity?.entity_name || selectedEntity?.value || selectedEntity?.label || '';
      
      const matchedResolvedField = sourceResolvedFields.find((f) => {
        const entityName = f.entity_name || f.entity || '';
        return entityName === entityValue || entityName === entityLabel;
      }) || sourceResolvedFields[index];
      
      if (matchedResolvedField) {
        const fieldsObj = matchedResolvedField.fields;
        if (fieldsObj) {
          let fieldsArray = [];
          if (fieldsObj.fields && Array.isArray(fieldsObj.fields)) {
            fieldsArray = fieldsObj.fields;
          } else if (Array.isArray(fieldsObj)) {
            fieldsArray = fieldsObj;
          }
          
          if (fieldsArray.length > 0) {
            grouped.push({
              entityName: entityLabel,
              fields: fieldsArray,
            });
          }
        }
      }
    });
    
    return grouped;
  }, [resolvedFields, selectedSourceEntitiesByWorkflow, sourceNodes, buildNodeKey]);

  const sourceFields = useMemo(() => {
    return sourceFieldsGrouped.flatMap((group) => group.fields);
  }, [sourceFieldsGrouped]);

  const destinationFieldsGrouped = useMemo(() => {
    if (!resolvedFields || !Array.isArray(resolvedFields)) {
      return [];
    }
    
    // Use Redux state - get selected entities from active destination
    const targetDestId = activeDestinationId || destNodes[0]?.id;
    const activeDest = destNodes.find(d => d.id === targetDestId);
    const destKey = activeDest ? buildNodeKey(activeDest) : null;
    const selectedDestEntities = destKey ? (selectedDestinationEntitiesByWorkflow[destKey] || []) : [];
    
    if (!selectedDestEntities || selectedDestEntities.length === 0) {
      return [];
    }
    
    // Filter by destination type and active destination nodeId (for many-to-one support)
    const destResolvedFields = resolvedFields.filter((f) => {
      if (f.type !== 'destination') return false;
      // If nodeId/node_id matches active destination, include it
      if (targetDestId && (f.nodeId === targetDestId || f.node_id === targetDestId)) return true;
      // If no nodeId filter, include all (backward compatibility)
      if (!targetDestId) return true;
      return false;
    });
    
    if (destResolvedFields.length === 0) {
      return [];
    }
    
    const grouped = [];
    
    selectedDestEntities.forEach((selectedEntity, index) => {
      const entityLabel = typeof selectedEntity === 'string' ? selectedEntity : selectedEntity?.label || selectedEntity?.entity_name || selectedEntity?.value || '';
      const entityValue = typeof selectedEntity === 'string' ? selectedEntity : selectedEntity?.entity_name || selectedEntity?.value || selectedEntity?.label || '';
      
      const matchedResolvedField = destResolvedFields.find((f) => {
        const entityName = f.entity_name || f.entity || '';
        return entityName === entityValue || entityName === entityLabel;
      }) || destResolvedFields[index];
      
      if (matchedResolvedField) {
        const fieldsObj = matchedResolvedField.fields;
        if (fieldsObj) {
          let fieldsArray = [];
          if (fieldsObj.fields && Array.isArray(fieldsObj.fields)) {
            fieldsArray = fieldsObj.fields;
          } else if (Array.isArray(fieldsObj)) {
            fieldsArray = fieldsObj;
          }
          
          if (fieldsArray.length > 0) {
            grouped.push({
              entityName: entityLabel,
              fields: fieldsArray,
            });
          }
        }
      }
    });
    
    return grouped;
  }, [resolvedFields, selectedDestinationEntitiesByWorkflow, activeDestinationId, destNodes, buildNodeKey]);

  const destinationFields = useMemo(() => {
    return destinationFieldsGrouped.flatMap((group) => group.fields);
  }, [destinationFieldsGrouped]);

  // Get auto-mapped fields
  const autoMappedSourceFields = useMemo(() => {
    if (!autoMappedFields || typeof autoMappedFields !== 'object') return [];
    return Object.keys(autoMappedFields);
  }, [autoMappedFields]);

  const autoMappedDestinationFields = useMemo(() => {
    if (!autoMappedFields || typeof autoMappedFields !== 'object') return [];
    return Object.values(autoMappedFields);
  }, [autoMappedFields]);

  // Determine step index
  const stepIndex = useMemo(() => {
    const stepMap = {
      selectDataset: 0,
      configureDataset: 1,
      entitySelection: 2,
      mapping: 3,
    };
    return stepMap[currentStep] || 0;
  }, [currentStep]);

  // Get selected node config
  const nodeConfig = selectedNode?.data?.config || {};

  const SelectIcon = (props) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: 20 }} />;

  // Set Properties state for bottom panel (only for source/destination nodes)
  const [propertiesState, setPropertiesState] = useState({
    connectionsList: [],
    selectedConnection: null,
    errors: {},
    selectedModule: '',
    moduleOptions: [],
    loadingConnections: false,
    loadingModules: false,
    selectedEnvironment: '',
    jiraProjects: [],
    loadingJiraProjects: false,
    selectedJiraProject: '',
    selectedJiraProjectDetails: null,
  });

  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext);

  // Helper to get node type from label
  const getNodeType = (node) => {
    if (!node) return '';
    const rawLabel = node?.data?.label || node?.data?.config?.label || '';
    return rawLabel.split('(')[0]?.trim()?.toLowerCase() || '';
  };

  const isJiraNode = useMemo(() => getNodeType(selectedNode) === 'jira', [selectedNode]);
  const isSqlNode = useMemo(() => getNodeType(selectedNode) === 'sql', [selectedNode]);
  const nodeType = useMemo(() => getNodeType(selectedNode), [selectedNode]);
  // Streaming connectors: Kafka, S3, Local File, SFTP
  const streamingConnectors = ['kafka', 's3', 'local file', 'localfile', 'sftp'];
  const isStreamingConnector = useMemo(() => streamingConnectors.includes(nodeType), [nodeType]);

  // Initialize properties when node is selected
  useEffect(() => {
    if (!selectedNode || currentStep !== 'configureDataset') return;
    if (selectedNode?.type !== 'sourceNode' && selectedNode?.type !== 'source' && 
        selectedNode?.type !== 'destinationNode' && selectedNode?.type !== 'destination') return;

    const initializeProperties = async () => {
      if (!nodeType) return;

      setPropertiesState(prev => ({
        ...prev,
        connectionsList: [],
        selectedConnection: null,
        selectedModule: '',
        moduleOptions: [],
        errors: {},
        loadingConnections: true,
        loadingModules: true,
      }));

      try {
        const connections = await fetchTypeBasedConnection(nodeType);
        
        let modules = [];
        if (!isJiraNode && !isStreamingConnector) {
          if (isSqlNode) {
            modules = ['SQL'];
          } else {
            modules = await fetchModulesBySource(nodeType);
          }
        }

        const config = selectedNode?.data?.config;
        let matchedConn = null;
        let env = '';

        if (config?.connection && connections.length > 0) {
          matchedConn = connections.find(
            (conn) => conn.conn_id === config.connection.conn_id
          );

          if (!matchedConn && config.connection.conn_name) {
            matchedConn = connections.find(
              (conn) => conn.conn_name === config.connection.conn_name
            );
          }

          if (matchedConn) {
            env = typeof matchedConn.environment === 'string'
              ? matchedConn.environment.toLowerCase()
              : '';
          }
        }

        setPropertiesState(prev => ({
          ...prev,
          connectionsList: connections,
          selectedConnection: matchedConn,
          selectedEnvironment: env,
          moduleOptions: modules,
          selectedModule: config?.module && !isJiraNode && !isStreamingConnector
            ? (isSqlNode ? config.module?.toUpperCase() : config.module)
            : (isSqlNode && !config?.module && !isStreamingConnector ? 'SQL' : ''),
          loadingConnections: false,
          loadingModules: false,
        }));
      } catch (error) {
        console.error('Error loading node properties:', error);
        setPropertiesState(prev => ({
          ...prev,
          loadingConnections: false,
          loadingModules: false,
        }));
      }
    };

    initializeProperties();
  }, [selectedNode, currentStep, nodeType, isJiraNode, isSqlNode]);

  // Fetch Jira projects when connection is selected
  useEffect(() => {
    if (!isJiraNode || !propertiesState.selectedConnection?.conn_id) return;

    const loadJiraProjects = async () => {
      setPropertiesState(prev => ({ ...prev, loadingJiraProjects: true }));
      try {
        const data = await fetchJiraProjects(propertiesState.selectedConnection.conn_id);
        setPropertiesState(prev => ({
          ...prev,
          jiraProjects: Array.isArray(data) ? data : [],
          loadingJiraProjects: false,
        }));
      } catch (error) {
        console.error('Error loading Jira projects:', error);
        setPropertiesState(prev => ({
          ...prev,
          jiraProjects: [],
          loadingJiraProjects: false,
        }));
      }
    };

    loadJiraProjects();
  }, [propertiesState.selectedConnection?.conn_id, isJiraNode]);

  // Helper functions
  const getConnectorName = (conn) =>
    (conn?.connector_name || conn?.connector || '').toLowerCase();

  const buildSqlMetadata = (conn) => {
    if (!conn) return null;
    if (getConnectorName(conn) !== 'sql') return null;
    const params = conn?.custom_params || {};
    return {
      token: params?.token || conn?.token || null,
      host: params?.host || '',
      port: params?.port ? String(params.port) : '',
      database: params?.database_name || params?.db_name || params?.database || '',
    };
  };

  const fetchConnections = async () => {
    if (!nodeType) return;
    setPropertiesState(prev => ({ ...prev, loadingConnections: true }));
    try {
      const connections = await fetchTypeBasedConnection(nodeType);
      setPropertiesState(prev => ({
        ...prev,
        connectionsList: connections,
        loadingConnections: false,
      }));
    } catch (error) {
      console.error('Error fetching connections:', error);
      setPropertiesState(prev => ({ ...prev, loadingConnections: false }));
    }
  };

  const fetchModules = async () => {
    if (isJiraNode || !nodeType) return;
    setPropertiesState(prev => ({ ...prev, loadingModules: true }));
    try {
      if (isSqlNode) {
        setPropertiesState(prev => ({
          ...prev,
          moduleOptions: ['SQL'],
          loadingModules: false,
        }));
        return;
      }
      const modules = await fetchModulesBySource(nodeType);
      setPropertiesState(prev => ({
        ...prev,
        moduleOptions: modules,
        loadingModules: false,
      }));
    } catch (error) {
      console.error('Error fetching modules:', error);
      setPropertiesState(prev => ({ ...prev, loadingModules: false }));
    }
  };

  const validateProperties = () => {
    const newErrors = {};
    if (!propertiesState.selectedConnection) {
      newErrors.connection = 'Connection is required';
    }
    if (!isJiraNode && !isStreamingConnector && !propertiesState.selectedModule) {
      newErrors.module = 'Module is required';
    }
    setPropertiesState(prev => ({ ...prev, errors: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProperties = () => {
    if (!validateProperties() || !updateNodeData || !selectedNode) return;

    const baseConfig = {
      id: selectedNode.id,
      label: selectedNode?.data?.label || selectedNode?.data?.config?.label,
      type: selectedNode.type,
      ...selectedNode?.data?.config,
      connection: propertiesState.selectedConnection
        ? {
            conn_id: propertiesState.selectedConnection.conn_id,
            conn_name: propertiesState.selectedConnection.conn_name,
            client_id: propertiesState.selectedConnection.client_id,
            api_version: propertiesState.selectedConnection.api_version,
          }
        : null,
    };

    if (!isJiraNode && !isStreamingConnector) {
      const normalizedModule = isSqlNode
        ? (propertiesState.selectedModule || 'SQL').toLowerCase()
        : propertiesState.selectedModule;
      baseConfig.module = normalizedModule;
    }

    if (isJiraNode && propertiesState.selectedJiraProjectDetails) {
      baseConfig.project = {
        key: propertiesState.selectedJiraProjectDetails.key,
        name: propertiesState.selectedJiraProjectDetails.name,
        id: propertiesState.selectedJiraProjectDetails.id,
      };
    }

    const sqlMetadata = buildSqlMetadata(propertiesState.selectedConnection);
    if (sqlMetadata) {
      baseConfig.sqlMetadata = sqlMetadata;
    }

    updateNodeData(selectedNode.id, baseConfig);
    toast.success('Properties saved successfully!');
  };

  // Dropdown styling
  const getDropdownStyle = (hasError = false) => ({
    mb: 2,
    borderRadius: '6px',
    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
    height: '2.8rem',
    width: '100%',
    color: darkMode ? '#ffffff' : theme.palette.text.primary,
    '& .MuiSelect-select': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
      padding: '12px 16px',
      fontSize: '0.95rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#404040' : '#DDDFE3',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#2196F3' : '#2196F3',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : '#2196F3',
    },
  });

  // Custom Step Connector with green styling - lines pass through center of circles
  const CustomStepConnector = styled(StepConnector)(() => ({
    '@keyframes drawLine': {
      '0%': {
        transform: 'scaleX(0)',
        transformOrigin: 'left',
      },
      '100%': {
        transform: 'scaleX(1)',
        transformOrigin: 'left',
      },
    },
    '@keyframes flowLine': {
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(400%)',
      },
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#4caf50',
        borderTopWidth: 1.5,
        transition: 'border-color 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-0.75px',
          left: 0,
          width: '30%',
          height: '1.5px',
          background: 'linear-gradient(90deg, transparent, #4caf50, #66bb6a, #4caf50, transparent)',
          animation: 'flowLine 1.5s linear infinite',
          boxShadow: '0 0 6px rgba(76, 175, 80, 0.6)',
        },
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#4caf50',
        borderTopWidth: 1.5,
        position: 'relative',
        overflow: 'hidden',
        animation: 'drawLine 0.5s ease forwards',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-0.75px',
          left: 0,
          width: '30%',
          height: '1.5px',
          background: 'linear-gradient(90deg, transparent, #4caf50, #66bb6a, #4caf50, transparent)',
          animation: 'flowLine 1.5s linear infinite',
          boxShadow: '0 0 6px rgba(76, 175, 80, 0.6)',
        },
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
      borderTopWidth: 1.5,
      borderRadius: 1,
      transition: 'border-color 0.3s ease',
      minHeight: 1.5,
    },
  }));

  // Custom Step Icon
  const CustomStepIcon = ({ active, completed }) => {
    return (
      <Box
        sx={{
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1, // Ensure circles appear above connector lines
          flexShrink: 0,
        }}
      >
        {completed ? (
          // Completed: Solid green circle
          <Box
            sx={{
              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
              borderRadius: '50%',
              bgcolor: '#4caf50',
              border: 'none',
              boxSizing: 'border-box',
              transition: 'all 0.3s ease',
              animation: 'fadeIn 0.5s ease',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'scale(0.8)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'scale(1)',
                },
              },
            }}
          />
        ) : active ? (
          // Active: Outlined green circle with inner solid circle
          <Box
            sx={{
              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
              borderRadius: '50%',
              border: '2px solid #4caf50',
              bgcolor: 'transparent',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              animation: 'activePulse 2s ease-in-out infinite',
              '@keyframes activePulse': {
                '0%, 100%': {
                  boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
                },
                '50%': {
                  boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)',
                },
              },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                minWidth: 10,
                minHeight: 10,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                flexShrink: 0,
                animation: 'innerPulse 1.5s ease-in-out infinite',
                '@keyframes innerPulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(0.8)',
                  },
                },
              }}
            />
          </Box>
        ) : (
          // Pending: Empty circle (gray)
          <Box
            sx={{
              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
              borderRadius: '50%',
              border: '2px solid #e0e0e0',
              bgcolor: 'transparent',
              boxSizing: 'border-box',
            }}
          />
        )}
      </Box>
    );
  };

  CustomStepIcon.propTypes = {
    active: PropTypes.bool,
    completed: PropTypes.bool,
    icon: PropTypes.node,
  };

  const renderProgressIndicator = () => {
    return (
      <Box
        sx={{
          px: 4,
          py: 2,
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Stepper
          activeStep={stepIndex}
          connector={<CustomStepConnector />}
          orientation="horizontal"
          sx={{
            '& .MuiStep-root': {
              flexDirection: 'column',
              alignItems: 'center',
            },
            '& .MuiStepLabel-root': {
              cursor: 'pointer',
              flexDirection: 'column',
              alignItems: 'center',
            },
            '& .MuiStepLabel-iconContainer': {
              paddingRight: 0,
              paddingBottom: 1,
              position: 'relative',
              zIndex: 1, // Ensure icon container is above connector
            },
            '& .MuiStepLabel-label': {
              fontSize: '0.75rem',
              fontWeight: 400,
              color: '#9e9e9e',
              textAlign: 'center',
              marginTop: 0,
              '&.Mui-active': {
                color: '#4caf50',
                fontWeight: 600,
              },
              '&.Mui-completed': {
                color: '#4caf50',
              },
            },
          }}
        >
          {STEP_LABELS.map((label, index) => {
            const isCompleted = index < stepIndex;
            const isActive = index === stepIndex;
            const canNavigate = index <= stepIndex;

            return (
              <Step
                key={label}
                completed={isCompleted}
                active={isActive}
                onClick={() => {
                  if (canNavigate && onStepChange) {
                    const stepMap = ['selectDataset', 'configureDataset', 'entitySelection', 'mapping'];
                    onStepChange(stepMap[index]);
                  }
                }}
                sx={{
                  cursor: canNavigate ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  '&:hover': canNavigate ? {
                    transform: 'translateY(-2px)',
                  } : {},
                }}
              >
                <StepLabel
                  StepIconComponent={CustomStepIcon}
                  onClick={(e) => {
                    if (canNavigate && onStepChange) {
                      e.stopPropagation();
                      const stepMap = ['selectDataset', 'configureDataset', 'entitySelection', 'mapping'];
                      onStepChange(stepMap[index]);
                    }
                  }}
                  sx={{
                    cursor: canNavigate ? 'pointer' : 'default',
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    );
  };

  const renderStepContent = (step = currentStep) => {
    switch (step) {
      case 'selectDataset':
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
              Drag and drop Source and Destination nodes from the left sidebar to get started.
            </Typography>
          </Box>
        );

      case 'configureDataset': {
        // Check if selected node is a function node
        if (selectedNode?.type === 'functionNode') {
          return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#666', fontSize: '0.875rem', mb: 2, fontWeight: 500 }}>
                Function Node Selected
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.875rem', mb: 2 }}>
                Function: <strong>{selectedNode?.data?.label || selectedNode?.data?.functionType || 'Unknown'}</strong>
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.75rem', mb: 3, fontStyle: 'italic' }}>
                Double-click the function node to configure it.
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                Tip: Connect the function node between source and destination nodes for field transformations.
              </Typography>
            </Box>
          );
        }

        // Show configuration even if no node is selected (for connected nodes)
        if (!selectedNode) {
          // Get all source and destination nodes
          const availableNodes = nodes.filter(
            (n) => n.type === 'sourceNode' || 
                   n.type === 'source' || 
                   n.type === 'destinationNode' || 
                   n.type === 'destination'
          );

          // If there are available nodes, show a selector
          if (availableNodes.length > 0) {
            return (
              <Box sx={{ p: 3 }}>
                <Typography 
                  sx={{ 
                    color: '#666', 
                    fontSize: '0.875rem',
                    mb: 3,
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  Select a node to configure its properties:
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2,
                  maxWidth: '800px',
                  mx: 'auto',
                }}>
                  {availableNodes.map((node) => {
                    const nodeLabel = node?.data?.label || node?.data?.config?.label || 'Unnamed Node';
                    const nodeType = node?.type || '';
                    const isSource = nodeType === 'sourceNode' || nodeType === 'source';
                    const hasConfig = node?.data?.config?.connection?.conn_id;
                    
                    return (
                      <Box
                        key={node.id}
                        onClick={() => {
                          if (onNodeSelect) {
                            onNodeSelect(node);
                          }
                        }}
                        sx={{
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#fff',
                          '&:hover': {
                            borderColor: '#2196f3',
                            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#212121',
                            mb: 1,
                          }}
                        >
                          {nodeLabel}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={isSource ? 'Source' : 'Destination'}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: '20px',
                              bgcolor: isSource ? '#e3f2fd' : '#f3e5f5',
                              color: isSource ? '#1976d2' : '#7b1fa2',
                            }}
                          />
                          {hasConfig && (
                            <Chip
                              label="Configured"
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#666',
                            fontStyle: 'italic',
                          }}
                        >
                          {hasConfig 
                            ? `Connection: ${node?.data?.config?.connection?.conn_name || 'N/A'}`
                            : 'Click to configure properties'
                          }
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography 
                    sx={{ 
                      color: '#2196f3', 
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': { color: '#1976d2' }
                    }}
                    onClick={() => onStepChange('entitySelection')}
                  >
                    Proceed to Entity Selection{' '}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        animation: 'slideRight 1.5s ease-in-out infinite',
                        '@keyframes slideRight': {
                          '0%, 100%': {
                            transform: 'translateX(0)',
                          },
                          '50%': {
                            transform: 'translateX(4px)',
                          },
                        },
                      }}
                    >
                      →
                    </Box>
                  </Typography>
                </Box>
              </Box>
            );
          }

          // No nodes available
          return (
            <Box sx={{ p: 3 }}>
              <Typography 
                sx={{ 
                  color: '#666', 
                  fontSize: '0.875rem',
                  mb: 2,
                  textAlign: 'center'
                }}
              >
                Select a node to configure its connection settings, or proceed to entity selection.
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  sx={{ 
                    color: '#2196f3', 
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: '#1976d2' }
                  }}
                  onClick={() => onStepChange('entitySelection')}
                >
                  Proceed to Entity Selection{' '}
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      animation: 'slideRight 1.5s ease-in-out infinite',
                      '@keyframes slideRight': {
                        '0%, 100%': {
                          transform: 'translateX(0)',
                        },
                        '50%': {
                          transform: 'translateX(4px)',
                        },
                      },
                    }}
                  >
                    →
                  </Box>
                </Typography>
              </Box>
            </Box>
          );
        }

        // Check if it's a source or destination node - show Set Properties
        const isSourceOrDest = selectedNode?.type === 'sourceNode' || 
                               selectedNode?.type === 'source' ||
                               selectedNode?.type === 'destinationNode' || 
                               selectedNode?.type === 'destination';

        if (isSourceOrDest && updateNodeData) {
          return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{ 
                  fontSize: 19, 
                  mb: 3,
                  fontWeight: 600,
                  color: darkMode ? '#ffffff' : 'text.primary',
                }}
              >
                Set Properties
              </Typography>

              {!isStreamingConnector && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 500 }}
                    color={darkMode ? '#ffffff' : 'text.primary'}
                  >
                    Select Environment
                  </Typography>
                  <Select
                    fullWidth
                    value={
                      ['test', 'production'].includes(propertiesState.selectedEnvironment)
                        ? propertiesState.selectedEnvironment
                        : ''
                    }
                    onChange={(e) => {
                      setPropertiesState(prev => ({
                        ...prev,
                        selectedEnvironment: e.target.value,
                        selectedConnection: null,
                      }));
                    }}
                    displayEmpty
                    sx={getDropdownStyle()}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    <MenuItem value="" disabled>
                      Select Environment
                    </MenuItem>
                    <MenuItem value="test">Test</MenuItem>
                    <MenuItem value="production">Production</MenuItem>
                  </Select>
                </>
              )}

              <Typography
                variant="body1"
                sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                color={darkMode ? '#ffffff' : 'text.primary'}
              >
                Select Connection
              </Typography>
              <Select
                fullWidth
                value={propertiesState.selectedConnection?.conn_id || ''}
                onOpen={fetchConnections}
                onChange={(e) => {
                  const selected = propertiesState.connectionsList.find(
                    (conn) => conn.conn_id === e.target.value
                  );
                  if (!selected) return;
                  
                  const sqlMetadata = buildSqlMetadata(selected);
                  
                  setPropertiesState(prev => ({
                    ...prev,
                    selectedConnection: selected,
                  }));

                  // Update node data immediately when connection changes
                  if (updateNodeData && selectedNode) {
                    updateNodeData(selectedNode.id, {
                      connection: {
                        conn_id: selected.conn_id,
                        conn_name: selected.conn_name,
                        client_id: selected.client_id,
                        api_version: selected.api_version,
                        connector_name: selected.connector_name || selected.connector || nodeType?.toUpperCase() || '',
                        environment: selected.environment,
                      },
                      sqlMetadata: sqlMetadata || null,
                    });
                  }
                }}
                displayEmpty
                sx={getDropdownStyle(Boolean(propertiesState.errors.connection))}
                error={Boolean(propertiesState.errors.connection)}
                IconComponent={KeyboardArrowDownIcon}
              >
                <MenuItem value="" disabled>
                  {propertiesState.loadingConnections ? (
                    <Box display="flex" alignItems="center">
                      <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                    </Box>
                  ) : (
                    'Select'
                  )}
                </MenuItem>

                {!propertiesState.loadingConnections &&
                  propertiesState.connectionsList
                    .filter((conn) => {
                      // For streaming connectors, don't filter by environment
                      if (isStreamingConnector) return true;
                      // For other connectors, filter by environment
                      return propertiesState.selectedEnvironment === 'production'
                        ? !conn.environment ||
                            conn.environment.toLowerCase() === 'production'
                        : conn.environment?.toLowerCase() ===
                            propertiesState.selectedEnvironment?.toLowerCase();
                    })
                    .map((conn) => (
                      <MenuItem key={conn.conn_id} value={conn.conn_id}>
                        {conn.conn_name}
                      </MenuItem>
                    ))}
              </Select>

              {propertiesState.errors.connection && (
                <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                  {propertiesState.errors.connection}
                </Typography>
              )}

              {!isJiraNode && !isStreamingConnector && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                    color={darkMode ? '#ffffff' : 'text.primary'}
                  >
                    Select Module
                  </Typography>
                  <Select
                    fullWidth
                    value={propertiesState.selectedModule || ''}
                    onChange={(e) => {
                      const selected = e.target.value;
                      const normalizedModule = isSqlNode
                        ? (selected || 'SQL').toLowerCase()
                        : selected;

                      setPropertiesState(prev => ({
                        ...prev,
                        selectedModule: selected,
                      }));

                      // Update node data immediately when module changes
                      if (updateNodeData && selectedNode && propertiesState.selectedConnection) {
                        const updatedConfig = {
                          id: selectedNode.id,
                          label: selectedNode?.data?.label || selectedNode?.data?.config?.label,
                          type: selectedNode.type,
                          ...selectedNode?.data?.config,
                          connection: propertiesState.selectedConnection
                            ? {
                                conn_id: propertiesState.selectedConnection.conn_id,
                                conn_name: propertiesState.selectedConnection.conn_name,
                                client_id: propertiesState.selectedConnection.client_id,
                                api_version: propertiesState.selectedConnection.api_version,
                              }
                            : null,
                          module: normalizedModule,
                        };
                        updateNodeData(selectedNode.id, updatedConfig);
                      }
                    }}
                    onOpen={fetchModules}
                    displayEmpty
                    sx={getDropdownStyle(Boolean(propertiesState.errors.module))}
                    error={Boolean(propertiesState.errors.module)}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    <MenuItem value="" disabled>
                      {propertiesState.loadingModules ? (
                        <Box display="flex" alignItems="center">
                          <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                        </Box>
                      ) : (
                        'Select'
                      )}
                    </MenuItem>
                    {!propertiesState.loadingModules &&
                      Array.isArray(propertiesState.moduleOptions) &&
                      propertiesState.moduleOptions.map((module) => (
                        <MenuItem key={module} value={module}>
                          {module}
                        </MenuItem>
                      ))}
                  </Select>
                  {propertiesState.errors.module && (
                    <Typography variant="caption" color="error" sx={{ mb: 2 }}>
                      {propertiesState.errors.module}
                    </Typography>
                  )}
                </>
              )}

              {isJiraNode && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                    color={darkMode ? '#ffffff' : 'text.primary'}
                  >
                    Select Jira Project
                  </Typography>
                  <Select
                    fullWidth
                    value={propertiesState.selectedJiraProject}
                    onChange={async (e) => {
                      const projectKey = e.target.value;
                      const selectedProjectObj = propertiesState.jiraProjects.find(
                        (p) => p.key === projectKey || p.id === projectKey
                      );

                      setPropertiesState(prev => ({
                        ...prev,
                        selectedJiraProject: projectKey,
                        selectedJiraProjectDetails: selectedProjectObj,
                      }));

                      if (selectedProjectObj && updateNodeData && selectedNode && propertiesState.selectedConnection) {
                        const updatedConfig = {
                          id: selectedNode.id,
                          label: selectedNode?.data?.label || selectedNode?.data?.config?.label,
                          type: selectedNode.type,
                          ...selectedNode?.data?.config,
                          connection: propertiesState.selectedConnection
                            ? {
                                conn_id: propertiesState.selectedConnection.conn_id,
                                conn_name: propertiesState.selectedConnection.conn_name,
                                client_id: propertiesState.selectedConnection.client_id,
                                api_version: propertiesState.selectedConnection.api_version,
                              }
                            : null,
                          project: {
                            key: selectedProjectObj.key,
                            name: selectedProjectObj.name,
                            id: selectedProjectObj.id,
                          },
                        };
                        updateNodeData(selectedNode.id, updatedConfig);

                        try {
                          await addProjectKeyToJiraConnection(
                            propertiesState.selectedConnection?.conn_id,
                            projectKey
                          );
                        } catch (error) {
                          console.error('Failed to add project key:', error);
                        }
                      }
                    }}
                    displayEmpty
                    sx={getDropdownStyle()}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    <MenuItem value="" disabled>
                      {propertiesState.loadingJiraProjects ? (
                        <Box display="flex" alignItems="center">
                          <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                        </Box>
                      ) : (
                        'Select'
                      )}
                    </MenuItem>

                    {!propertiesState.loadingJiraProjects &&
                      propertiesState.jiraProjects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                  </Select>
                </>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    if (onNodeSelect) {
                      onNodeSelect(null);
                    }
                    // Reset to initial state
                    setPropertiesState(prev => ({
                      ...prev,
                      errors: {},
                    }));
                  }}
                  sx={{
                    borderRadius: '0.5rem',
                    padding: '0.3rem 0.8rem',
                    fontSize: '0.875rem',
                    color: '#5A66A6',
                    border: 'none',
                    background: 'transparent',
                    '&:hover': {
                      border: '1px solid #5A66A6',
                      backgroundColor: darkMode
                        ? 'rgba(79, 172, 254, 0.1)'
                        : 'rgba(79, 172, 254, 0.05)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <PrimaryButton
                  variant="contained"
                  size="small"
                  onClick={handleSaveProperties}
                  sx={{
                    borderRadius: '0.5rem',
                    padding: '0.3rem 0.8rem',
                    fontSize: '0.875rem',
                  }}
                >
                  Save
                </PrimaryButton>
              </Box>
            </Box>
          );
        }

        // Fallback to original read-only display for other cases
        return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2.5,
          p: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Open Sans, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              color: '#383838',
            }}
          >
                Connector Type
          </Typography>
          <TextField
            size="small"
            fullWidth
                value={nodeConfig?.connector_name || nodeConfig?.label || ''}
            inputProps={{ readOnly: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px !important',
              },
            }}
          />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Open Sans, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              color: '#383838',
            }}
          >
                Connection
          </Typography>
              <TextField
            size="small"
            fullWidth
                placeholder="Select Existing Connection"
                value={nodeConfig?.connection?.conn_name || ''}
                inputProps={{ readOnly: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px !important',
                },
              }}
              />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Open Sans, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              color: '#383838',
            }}
          >
                API Base URL
          </Typography>
              <TextField
            size="small"
            fullWidth
                placeholder="e.g., user_age"
            sx={{
              '& .MuiOutlinedInput-root': {
                    borderRadius: '4px !important',
                  },
                }}
              />
            </Box>
          </Box>
        );
      }

      case 'entitySelection':
        return (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mb: 3,
              }}
            >
              {/* Source Entities - Show per-source selectors for Many-to-One support */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sourceNodes.length > 1 ? (
                  // Multiple sources: show selector for each
                  sourceNodes.map((sourceNode) => {
                    const sourceKey = buildNodeKey(sourceNode);
                    const availableEntities = sourceEntitiesByWorkflow[sourceKey] || [];
                    const selectedEntities = selectedSourceEntitiesByWorkflow[sourceKey] || [];
                    const sourceNodeLabel = sourceNode?.data?.config?.label || sourceNode?.data?.label || sourceNode.id;
                    
                    return (
                      <Box key={sourceNode.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Open Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#383838',
                          }}
                        >
                          {sourceNodeLabel} (Source)
                        </Typography>
                        {availableEntities.length > 0 ? (
                          <EntityMultiSelect
                            value={selectedEntities.map((e) => {
                              const entityName = typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '';
                              return typeof e === 'string' ? e : { value: entityName, label: e?.label || entityName };
                            })}
                            onChange={(event, newValue) => {
                              const entities = (newValue || []).map((val) => {
                                if (typeof val === 'string') {
                                  const entity = availableEntities.find(
                                    (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === val
                                  );
                                  return entity || val;
                                }
                                const entity = availableEntities.find(
                                  (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === (val?.value || val?.label)
                                );
                                return entity || val;
                              });
                              dispatch(
                                setSelectedSourceEntities({
                                  workflowId: effectiveWorkflowId,
                                  key: sourceKey,
                                  entities,
                                })
                              );
                            }}
                            options={availableEntities.map((entity) => ({
                              value: typeof entity === 'string' ? entity : entity?.name || entity?.entity_name || '',
                              label: typeof entity === 'string' ? entity : entity?.label || entity?.name || entity?.entity_name || '',
                            }))}
                            placeholder={`Select ${sourceNodeLabel} Entity`}
                            sx={{
                              mb: 0,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px !important',
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="No entities available. Configure connection first."
                            value=""
                            disabled
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px !important',
                              },
                            }}
                          />
                        )}
                      </Box>
                    );
                  })
                ) : (
                  // Single source: use Redux (consistent with multiple sources)
                  sourceNodes.length === 1 ? (() => {
                    const sourceNode = sourceNodes[0];
                    const sourceKey = buildNodeKey(sourceNode);
                    const availableEntities = sourceEntitiesByWorkflow[sourceKey] || [];
                    const selectedEntities = selectedSourceEntitiesByWorkflow[sourceKey] || [];
                    
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Open Sans, sans-serif',
                            fontWeight: 400,
                            fontSize: '14px',
                            color: '#383838',
                          }}
                        >
                          Source Entity
                        </Typography>
                        {availableEntities.length > 0 ? (
                          <EntityMultiSelect
                            value={selectedEntities.map((e) => {
                              const entityName = typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '';
                              return typeof e === 'string' ? e : { value: entityName, label: e?.label || entityName };
                            })}
                            onChange={(event, newValue) => {
                              const entities = (newValue || []).map((val) => {
                                if (typeof val === 'string') {
                                  const entity = availableEntities.find(
                                    (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === val
                                  );
                                  return entity || val;
                                }
                                const entity = availableEntities.find(
                                  (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === (val?.value || val?.label)
                                );
                                return entity || val;
                              });
                              dispatch(
                                setSelectedSourceEntities({
                                  workflowId: effectiveWorkflowId,
                                  key: sourceKey,
                                  entities,
                                })
                              );
                            }}
                            options={availableEntities.map((entity) => ({
                              value: typeof entity === 'string' ? entity : entity?.name || entity?.entity_name || '',
                              label: typeof entity === 'string' ? entity : entity?.label || entity?.name || entity?.entity_name || '',
                            }))}
                            placeholder="Select Source Entity"
                            sx={{
                              mb: 0,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px !important',
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="No entities available. Configure connection first."
                            value=""
                            disabled
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px !important',
                              },
                            }}
                          />
                        )}
                    {sourceFieldsGrouped.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#666',
                            mb: 0.5,
                          }}
                        >
                          Fields:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {sourceFieldsGrouped.map((group, groupIdx) => (
                            <Box key={groupIdx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#383838',
                                  mb: 0.25,
                                }}
                              >
                                {group.entityName}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1.5 }}>
                                {group.fields.slice(0, 10).map((field, idx) => {
                                  const fieldName = field?.field_name || field?.name || field;
                                  const isAutoMapped = autoMappedSourceFields.some((mapped) =>
                                    mapped.includes(fieldName)
                                  );
                                  return (
                                    <Chip
                                      key={idx}
                                      label={fieldName}
                                      size="small"
                                      sx={{
                                        fontSize: '11px',
                                        bgcolor: isAutoMapped ? '#e8f5e9' : '#f5f5f5',
                                        color: isAutoMapped ? '#2e7d32' : '#666',
                                        border: isAutoMapped ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                    );
                  })() : null
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Destination Selector for Many-to-One */}
                {destNodes.length > 1 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Open Sans, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#383838',
                        mb: 1,
                      }}
                    >
                      Select Destination Node
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={activeDestinationId || ''}
                      onChange={(e) => {
                        setActiveDestinationId(e.target.value);
                        // Clear destination entity selection when switching destinations (Redux only)
                        // Clear old destination
                        if (activeDestinationId) {
                          const oldDest = destNodes.find(d => d.id === activeDestinationId);
                          if (oldDest) {
                            const oldDestKey = buildNodeKey(oldDest);
                            dispatch(
                              setSelectedDestinationEntities({
                                workflowId: effectiveWorkflowId,
                                key: oldDestKey,
                                entities: [],
                              })
                            );
                          }
                        }
                        // Clear new destination
                        const newDest = destNodes.find(d => d.id === e.target.value);
                        if (newDest) {
                          const newDestKey = buildNodeKey(newDest);
                          dispatch(
                            setSelectedDestinationEntities({
                              workflowId: effectiveWorkflowId,
                              key: newDestKey,
                              entities: [],
                            })
                          );
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px !important',
                        },
                      }}
                    >
                      {destNodes.map((dest) => (
                        <MenuItem key={dest.id} value={dest.id}>
                          {dest?.data?.config?.label || dest?.data?.label || dest.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
                <Typography
                  sx={{
                    fontFamily: 'Open Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    color: '#383838',
                  }}
                >
                  Destination Entity
                </Typography>
                {availableDestinationEntities.length > 0 ? (
                  <EntityMultiSelect
                    value={(selectedDestinationEntitiesByWorkflow[activeDestNodeKey] || []).map((e) => {
                      const entityName = typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '';
                      return typeof e === 'string' ? e : { value: entityName, label: e?.label || entityName };
                    })}
                    onChange={(event, newValue) => {
                      // Use Redux only (consistent approach)
                      if (activeDestNodeKey) {
                        const entities = (newValue || []).map((val) => {
                          if (typeof val === 'string') {
                            const entity = availableDestinationEntities.find(
                              (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === val
                            );
                            return entity || val;
                          }
                          const entity = availableDestinationEntities.find(
                            (e) => (typeof e === 'string' ? e : e?.entity_name || e?.name || e?.label || '') === (val?.value || val?.label)
                          );
                          return entity || val;
                        });
                        dispatch(
                          setSelectedDestinationEntities({
                            workflowId: effectiveWorkflowId,
                            key: activeDestNodeKey,
                            entities,
                          })
                        );
                      }
                    }}
                    options={availableDestinationEntities.map((entity) => ({
                      value: typeof entity === 'string' ? entity : entity?.name || entity?.entity_name || '',
                      label: typeof entity === 'string' ? entity : entity?.label || entity?.name || entity?.entity_name || '',
                    }))}
                    placeholder="Select Destination Entity"
                    sx={{
                      mb: 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px !important',
                      },
                    }}
                  />
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="No entities available. Configure connection first."
                    value=""
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px !important',
                      },
                    }}
                  />
                )}
                {destinationFieldsGrouped.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#666',
                        mb: 0.5,
                      }}
                    >
                      Fields:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {destinationFieldsGrouped.map((group, groupIdx) => (
                        <Box key={groupIdx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#383838',
                              mb: 0.25,
                            }}
                          >
                            {group.entityName}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1.5 }}>
                            {group.fields.slice(0, 10).map((field, idx) => {
                              const fieldName = field?.field_name || field?.name || field;
                              const isAutoMapped = autoMappedDestinationFields.some((mapped) =>
                                mapped.includes(fieldName)
                              );
                              return (
                                <Chip
                                  key={idx}
                                  label={fieldName}
                                  size="small"
                                  sx={{
                                    fontSize: '11px',
                                    bgcolor: isAutoMapped ? '#e8f5e9' : '#f5f5f5',
                                    color: isAutoMapped ? '#2e7d32' : '#666',
                                    border: isAutoMapped ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={autoFuzzyEnabled}
                  onChange={(e) =>
                    dispatch(
                      setAutoFuzzyEnabled({
                        workflowId: effectiveWorkflowId,
                        enabled: e.target.checked,
                      }),
                    )
                  }
                />
              }
              label="Auto Fuzzy"
              sx={{ mt: 1 }}
            />
            
            {/* Proceed to Mapping Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryButton
                variant="contained"
                onClick={handleProceedToMapping}
                disabled={(() => {
                  // Check if all source nodes have entities (Many-to-One support) - use Redux only
                  const allSourceNodesHaveEntities = sourceNodes.every((sourceNode) => {
                    const sourceKey = buildNodeKey(sourceNode);
                    const entities = selectedSourceEntitiesByWorkflow[sourceKey] || [];
                    return entities.length > 0;
                  });
                  
                  // Check if destination has entities - use Redux only
                  const targetDestId = activeDestinationId || destNodes[0]?.id;
                  const activeDest = destNodes.find(d => d.id === targetDestId);
                  const destKey = activeDest ? buildNodeKey(activeDest) : null;
                  const destEntities = destKey ? (selectedDestinationEntitiesByWorkflow[destKey] || []) : [];
                  const destHasEntities = destEntities.length > 0;
                  
                  return !allSourceNodesHaveEntities || !destHasEntities;
                })()}
                sx={{ minWidth: 150 }}
              >
                Proceed to Mapping
              </PrimaryButton>
            </Box>
          </Box>
        );

      case 'mapping':
        // Check if selected node is a function node
        if (selectedNode?.type === 'functionNode') {
          return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#666', fontSize: '0.875rem', mb: 2, fontWeight: 500 }}>
                Function Node Selected
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.875rem', mb: 2 }}>
                Function: <strong>{selectedNode?.data?.label || selectedNode?.data?.functionType || 'Unknown'}</strong>
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.75rem', mb: 3, fontStyle: 'italic' }}>
                Double-click the function node to configure it.
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                Tip: Connect the function node between source and destination nodes for field transformations.
              </Typography>
            </Box>
          );
        }

        if (!mappingData && !resolvedFields) {
          return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                Complete entity selection to start mapping fields.
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Mapping Rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {mappingRows.map((row, rowIndex) => (
                <Box
                  key={row.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#fafafa',
                    position: 'relative',
                  }}
                >
                  {/* Remove button for rows after the first */}
                  {rowIndex > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => removeMappingRow(row.id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#d32f2f',
                        '&:hover': { bgcolor: '#ffebee' },
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      gap: 2.5,
                    }}
                  >
                    {/* Source Field */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Open Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#383838',
                        }}
                      >
                        Selected Field
                      </Typography>
                      {sourceFieldsGrouped.length > 0 ? (
                        <GroupedFieldSelect
                          key={`source-${row.id}-${currentStep}`}
                          value={row.sourceField || null}
                          onChange={(event, newValue) => {
                            updateMappingRow(row.id, 'sourceField', newValue || '');
                          }}
                          groupedFields={sourceFieldsGrouped}
                          placeholder={`Select Field ${sourceFields.length > 0 ? `(${sourceFields.length})` : '(0)'}`}
                          sx={{ width: '100%', mb: 0 }}
                          disabled={sourceFields.length === 0}
                        />
                      ) : (
                        <FilterSelect
                          type="autocomplete"
                          value={row.sourceField || null}
                          onChange={(event, newValue) => {
                            updateMappingRow(row.id, 'sourceField', newValue || '');
                          }}
                          options={['No fields available']}
                          placeholder="Select Field (0)"
                          sx={{ width: '100%', mb: 0 }}
                          disabled={true}
                        />
                      )}
                    </Box>

                    {/* Function Chain (Optional) - Multi-function support */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Open Sans, sans-serif',
                            fontWeight: 400,
                            fontSize: '14px',
                            color: '#383838',
                          }}
                        >
                          Function Chain (Optional)
                        </Typography>
                        {(row.functionChain && row.functionChain.length > 0) && (
                          <Chip
                            label={`${row.functionChain.length} function(s)`}
                            size="small"
                            color="primary"
                            sx={{ fontSize: '0.75rem', height: 24 }}
                          />
                        )}
                      </Box>

                      {/* Function Chain Items */}
                      {row.functionChain && row.functionChain.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {row.functionChain
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((func, funcIndex) => {
                              return (
                                <React.Fragment key={func.id}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '8px',
                                      bgcolor: '#fafafa',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                    }}
                                  >
                                    {/* Function Order Badge */}
                                    <Chip
                                      label={(func.order !== undefined ? func.order : funcIndex) + 1}
                                      size="small"
                                      sx={{
                                        minWidth: 32,
                                        fontWeight: 600,
                                        bgcolor: '#e3f2fd',
                                        color: '#1976d2'
                                      }}
                                    />

                                    {/* Function Selector - Cascading dropdown like single function */}
                                    <Box sx={{ flex: 1 }}>
                                      {!func.functionType || func.functionType === '' ? (
                                        <FormControl size="small" fullWidth>
                                          <Select
                                            displayEmpty
                                            value={func.functionType || ''}
                                            onChange={(e) => {
                                              const category = e.target.value;
                                              if (category && Object.keys(groupedFunctions).includes(category)) {
                                                updateFunctionInChain(row.id, funcIndex, {
                                                  ...func,
                                                  functionType: category,
                                                  functionName: '',
                                                  config: {}
                                                });
                                              }
                                            }}
                                            IconComponent={SelectIcon}
                                            renderValue={(val) => {
                                              if (!val || val === '') {
                                                return 'Select Function Type (Optional)';
                                              }
                                              return val;
                                            }}
                                            sx={{
                                              '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px !important',
                                              },
                                            }}
                                          >
                                            <MenuItem disabled value="">
                                              Select Function Type (Optional)
                                            </MenuItem>
                                            {Object.keys(groupedFunctions).map((type) => (
                                              <MenuItem key={type} value={type}>
                                                {type}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ) : (
                                        <FormControl size="small" fullWidth>
                                          <Select
                                            displayEmpty
                                            value={func.functionName || ''}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (value === '__back__') {
                                                updateFunctionInChain(row.id, funcIndex, {
                                                  ...func,
                                                  functionType: '',
                                                  functionName: '',
                                                  config: {}
                                                });
                                              } else if (value) {
                                                updateFunctionInChain(row.id, funcIndex, {
                                                  ...func,
                                                  functionName: value,
                                                  config: functionNeedsConfig(value) ? (func.config || {}) : {}
                                                });
                                              }
                                            }}
                                            IconComponent={SelectIcon}
                                            renderValue={(val) => {
                                              if (!val || val === '') {
                                                return `Select Function (${groupedFunctions[func.functionType]?.length || 0})`;
                                              }
                                              const funcDef = functionList.find((f) => f.id === val);
                                              return funcDef ? funcDef.label : val;
                                            }}
                                            sx={{
                                              '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px !important',
                                              },
                                            }}
                                          >
                                            <MenuItem 
                                              value="__back__"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateFunctionInChain(row.id, funcIndex, {
                                                  ...func,
                                                  functionType: '',
                                                  functionName: '',
                                                  config: {}
                                                });
                                              }}
                                              sx={{ fontStyle: 'italic', color: '#9ca3af' }}
                                            >
                                              ← Back to Types
                                            </MenuItem>
                                            {groupedFunctions[func.functionType]?.map((funcId) => {
                                              const funcDef = functionList.find((f) => f.id === funcId);
                                              return funcDef ? (
                                                <MenuItem key={funcId} value={funcId}>
                                                  {funcDef.label}
                                                </MenuItem>
                                              ) : null;
                                            })}
                                          </Select>
                                        </FormControl>
                                      )}
                                    </Box>

                                    {/* Action Buttons */}
                                    {func.functionName && (
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleFunctionConfig(row.id, funcIndex)}
                                        title="Configure function"
                                        color={func.showConfig ? 'primary' : 'default'}
                                        sx={{
                                          bgcolor: func.showConfig ? '#e3f2fd' : 'transparent',
                                          '&:hover': {
                                            bgcolor: func.showConfig ? '#bbdefb' : 'rgba(0, 0, 0, 0.04)'
                                          }
                                        }}
                                      >
                                        <SettingsIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    
                                    {/* Config indicator - show if function has config */}
                                    {func.functionName && func.config && Object.keys(func.config).length > 0 && (
                                      <Chip
                                        label="✓"
                                        size="small"
                                        sx={{
                                          minWidth: 24,
                                          height: 24,
                                          bgcolor: '#4caf50',
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}
                                        title="Function has configuration"
                                      />
                                    )}
                                    
                                    <IconButton
                                      size="small"
                                      onClick={() => moveFunctionInChain(row.id, funcIndex, 'up')}
                                      disabled={funcIndex === 0}
                                      title="Move up"
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    
                                    <IconButton
                                      size="small"
                                      onClick={() => moveFunctionInChain(row.id, funcIndex, 'down')}
                                      disabled={funcIndex === row.functionChain.length - 1}
                                      title="Move down"
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                    
                                    <IconButton
                                      size="small"
                                      onClick={() => removeFunctionFromChain(row.id, funcIndex)}
                                      color="error"
                                      title="Remove function"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>

                                  {/* Function Configuration Panel */}
                                  {func.showConfig && func.functionName && functionNeedsConfig(func.functionName) && (
                                    <Box sx={{ 
                                      ml: 4, 
                                      p: 2, 
                                      bgcolor: '#f9fafb', 
                                      borderRadius: '8px',
                                      border: '1px solid #e5e7eb'
                                    }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography sx={{ 
                                          fontSize: '0.875rem', 
                                          fontWeight: 600, 
                                          color: '#374151'
                                        }}>
                                          Function Configuration
                                        </Typography>
                                      </Box>
                                      {renderFunctionConfigForChain(row.id, funcIndex, func)}
                                      <Box sx={{ 
                                        mt: 2, 
                                        pt: 2, 
                                        borderTop: '1px solid #e5e7eb', 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        gap: 1
                                      }}>
                                        <Typography sx={{ 
                                          fontSize: '0.75rem', 
                                          color: '#6b7280', 
                                          fontStyle: 'italic'
                                        }}>
                                          Configure the function parameters above
                                        </Typography>
                                        <PrimaryButton
                                          variant="contained"
                                          size="small"
                                          onClick={() => {
                                            // Close the config panel
                                            toggleFunctionConfig(row.id, funcIndex);
                                            toast.success('Function configuration saved');
                                          }}
                                          sx={{ 
                                            fontSize: '0.875rem',
                                            minWidth: '130px',
                                            fontWeight: 500,
                                            textTransform: 'none'
                                          }}
                                        >
                                          Save & Close
                                        </PrimaryButton>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Chain Arrow (except last) */}
                                  {funcIndex < row.functionChain.length - 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: -0.5 }}>
                                      <ArrowForwardIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                                    </Box>
                                  )}
                                </React.Fragment>
                              );
                            })}
                        </Box>
                      ) : null
                      }

                      {/* Add Function Button */}
                      <Box>
                        <PrimaryButton
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addFunctionToChain(row.id)}
                          sx={{ fontSize: '0.875rem' }}
                        >
                          Add Function
                        </PrimaryButton>
                      </Box>
                    </Box>

                    {/* Destination Field */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Open Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#383838',
                        }}
                      >
                        Map Field
                      </Typography>
                      {(() => {
                        // Get all destination fields already selected in other rows
                        const selectedDestinationFields = mappingRows
                          .filter(r => r.id !== row.id && r.destinationField)
                          .map(r => r.destinationField);
                        
                        // Filter grouped fields to exclude already selected fields
                        const filteredGroupedFields = destinationFieldsGrouped.map((group) => ({
                          ...group,
                          fields: group.fields.filter((field) => {
                            const fieldName = field?.field_name || field?.name || field;
                            return !selectedDestinationFields.includes(fieldName) || row.destinationField === fieldName;
                          }),
                        })).filter((group) => group.fields.length > 0);
                        
                        if (filteredGroupedFields.length > 0) {
                          return (
                            <GroupedFieldSelect
                              key={`dest-${row.id}-${currentStep}`}
                              value={row.destinationField || null}
                              onChange={(event, newValue) => {
                                updateMappingRow(row.id, 'destinationField', newValue || '');
                              }}
                              groupedFields={filteredGroupedFields}
                              placeholder={`Select Field ${destinationFields.length > 0 ? `(${destinationFields.length})` : '(0)'}`}
                              sx={{ width: '100%', mb: 0 }}
                              disabled={destinationFields.length === 0}
                            />
                          );
                        }
                        
                        return (
                          <FilterSelect
                            type="autocomplete"
                            value={row.destinationField || null}
                            onChange={(event, newValue) => {
                              updateMappingRow(row.id, 'destinationField', newValue || '');
                            }}
                            options={['No fields available']}
                            placeholder="Select Field (0)"
                            sx={{ width: '100%', mb: 0 }}
                            disabled={true}
                          />
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Function Configuration Section - appears when function needs config */}
                  {row.function && functionNeedsConfig(row.function) && (row.showFunctionConfig !== false) && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      bgcolor: '#f9fafb', 
                      borderRadius: '8px', 
                      border: '1px solid #e5e7eb' 
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ 
                          fontSize: '0.875rem', 
                          fontWeight: 600, 
                          color: '#374151'
                        }}>
                          Function Configuration
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            updateMappingRow(row.id, 'showFunctionConfig', false);
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          <ExpandLessIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {renderFunctionConfig(row)}
                      <Box sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: '1px solid #e5e7eb', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography sx={{ 
                          fontSize: '0.75rem', 
                          color: '#6b7280', 
                          fontStyle: 'italic'
                        }}>
                          Configure the function parameters above
                        </Typography>
                        <PrimaryButton
                          variant="contained"
                          size="small"
                          onClick={() => {
                            // Close the config panel
                            updateMappingRow(row.id, 'showFunctionConfig', false);
                            toast.success('Function configuration saved');
                          }}
                          sx={{ 
                            fontSize: '0.875rem',
                            minWidth: '130px',
                            fontWeight: 500,
                            textTransform: 'none'
                          }}
                        >
                          Save & Close
                        </PrimaryButton>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Show config button if config panel is closed but function has config */}
                  {row.function && functionNeedsConfig(row.function) && row.showFunctionConfig === false && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <PrimaryButton
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => {
                          updateMappingRow(row.id, 'showFunctionConfig', true);
                        }}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Configure Function
                      </PrimaryButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Add Another Mapping Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
              <PrimaryButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addMappingRow}
                sx={{ fontSize: '0.875rem' }}
              >
                Add Another Mapping
              </PrimaryButton>
            </Box>
            
            {/* Discard, Preview, Visual Builder, and Save All Mappings Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, px: { xs: 2, md: 3 } }}>
              <Tooltip title="Discard Mapping" arrow>
                <IconButton
                  onClick={() => setDiscardConfirmOpen(true)}
                  sx={{ 
                    color: '#0F172A',
                    bgcolor: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: '8px',
                    padding: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      border: '1px solid rgba(211, 47, 47, 0.2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(211, 47, 47, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(211, 47, 47, 0.1)',
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Preview Mappings" arrow>
                <IconButton
                  onClick={async () => {
                    try {
                      // Get saved mappings from backend
                      let savedMappingsFromBackend = [];
                      try {
                        const result = await dispatch(getSavedMapping(effectiveWorkflowId)).unwrap();
                        savedMappingsFromBackend = result?.data?.mappings || [];
                      } catch (err) {
                        console.warn('Could not fetch saved mappings from backend:', err);
                      }
                      
                      // Get current mappings from Redux state (includes auto-fuzzy)
                      const currentState = store.getState();
                      const reduxMappings = currentState?.mapping?.mappingsByWorkflow?.[effectiveWorkflowId]?.connectionDetails || [];
                      
                      // Merge both: Redux mappings take precedence (they're more current)
                      // Create a map to deduplicate by sourceFieldId + destinationFieldId
                      const mappingMap = new Map();
                      
                      // First add saved mappings from backend
                      savedMappingsFromBackend.forEach(mapping => {
                        const key = `${mapping.sourceFieldId || mapping.sourceLabel}_${mapping.destinationFieldId || mapping.destinationLabel}`;
                        if (!mappingMap.has(key)) {
                          mappingMap.set(key, mapping);
                        }
                      });
                      
                      // Then add/override with Redux mappings (more current, includes auto-fuzzy)
                      reduxMappings.forEach(mapping => {
                        const key = `${mapping.sourceFieldId || mapping.sourceLabel}_${mapping.destinationFieldId || mapping.destinationLabel}`;
                        mappingMap.set(key, mapping);
                      });
                      
                      // Convert map back to array
                      const allMappings = Array.from(mappingMap.values());
                      setSavedMappings(allMappings);
                      setPreviewOpen(true);
                    } catch (error) {
                      console.error('Failed to load mappings:', error);
                      toast.error('Failed to load mappings');
                    }
                  }}
                  sx={{ 
                    color: '#3B9562',
                    bgcolor: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: '8px',
                    padding: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      backgroundColor: 'rgba(59, 149, 98, 0.08)',
                      border: '1px solid rgba(59, 149, 98, 0.2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(59, 149, 98, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(59, 149, 98, 0.1)',
                    }
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <PrimaryButton
                variant="outlined"
                size="small"
                onClick={() => {
                  // This will be handled by parent component (ETLWorkflow)
                  // We'll use a callback prop to switch to Mapping tab
                  if (onSwitchToVisualBuilder) {
                    onSwitchToVisualBuilder();
                  }
                }}
                sx={{ 
                  fontSize: '0.875rem',
                  color: '#0F172A',
                  borderColor: '#0F172A',
                  fontWeight: 500,
                  padding: '6px 14px',
                  borderRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#0F172A',
                    color: '#FFFFFF',
                    borderColor: '#0F172A',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(15, 23, 42, 0.25)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 4px rgba(15, 23, 42, 0.2)',
                  }
                }}
              >
                Visual Builder
              </PrimaryButton>
              <Tooltip title="Save All Mappings" arrow>
                <IconButton
                  onClick={async () => {
                  // Validate all rows
                  const invalidRows = mappingRows.filter(row => !row.sourceField || !row.destinationField);
                  if (invalidRows.length > 0) {
                    toast.error('Please select both source and destination fields for all mappings');
                    return;
                  }
                  
                  try {
                    // Get source and destination node info
                    const sourceNode = sourceNodes[0];
                    const destNode = destNodes[0];
                    
                    if (!sourceNode || !destNode) {
                      toast.error('Source or destination node not found');
                      return;
                    }
                    
                    // Get entity names from resolvedFields
                    const sourceEntityEntry = resolvedFields?.find((f) => f.type === 'source');
                    const destEntityEntry = resolvedFields?.find((f) => f.type === 'destination');
                    
                    const sourceEntityName = sourceEntityEntry?.entity_name || sourceEntityEntry?.entity || '';
                    const destEntityName = destEntityEntry?.entity_name || destEntityEntry?.entity || '';
                    
                    // Create a key function to identify duplicate mappings
                    const keyOf = (m) => {
                      const destNodeId = m.destinationNodeId || destNode.id;
                      
                      const getEntityName = (parentId) => {
                        if (!parentId) return '';
                        if (parentId.includes('__')) {
                          return parentId.split('__').slice(-1)[0];
                        }
                        return parentId;
                      };
                      
                      const getFieldName = (fieldId) => {
                        if (!fieldId) return '';
                        if (fieldId.includes('__')) {
                          return fieldId.split('__').slice(-1)[0];
                        }
                        return fieldId;
                      };
                      
                      const destEntity = getEntityName(m.destinationParentId);
                      const destField = getFieldName(m.destinationFieldId);
                      const srcEntity = getEntityName(m.sourceParentId);
                      const srcField = getFieldName(m.sourceFieldId);
                      
                      return `${destNodeId}::${destEntity}::${destField}::${srcEntity}::${srcField}`;
                    };
                    
                    // Get existing connectionDetails from Redux state
                    const state = store.getState();
                    const existingMappings = state?.mapping?.mappingsByWorkflow?.[effectiveWorkflowId]?.connectionDetails || [];
                    
                    // Use Map to deduplicate
                    const mergedMap = new Map();
                    
                    // Add existing mappings first
                    existingMappings.forEach((m) => {
                      const key = keyOf(m);
                      mergedMap.set(key, m);
                    });
                    
                    // Process all mapping rows
                    const newMappings = [];
                    for (const row of mappingRows) {
                      // Find the field objects from the arrays
                      const sourceFieldObj = sourceFields.find(
                        (f) => (f?.field_name || f?.name || f) === row.sourceField
                      );
                      const destFieldObj = destinationFields.find(
                        (f) => (f?.field_name || f?.name || f) === row.destinationField
                      );
                      
                      if (!sourceFieldObj || !destFieldObj) {
                        toast.error(`Could not find field details for row ${mappingRows.indexOf(row) + 1}. Please try selecting fields again.`);
                        continue;
                      }
                      
                      // Get field names
                      const sourceFieldName = sourceFieldObj.field_name || sourceFieldObj.name || row.sourceField;
                      const destFieldName = destFieldObj.field_name || destFieldObj.name || row.destinationField;
                      
                      // Format: nodeId__entityName (matches etl-frontend production format)
                      const sourceParentId = `${sourceNode.id}__${sourceEntityName}`;
                      const destinationParentId = `${destNode.id}__${destEntityName}`;
                      
                      // Format: nodeId__entityName__fieldName (matches etl-frontend production format)
                      const sourceFieldId = `${sourceNode.id}__${sourceEntityName}__${sourceFieldName}`;
                      const destinationFieldId = `${destNode.id}__${destEntityName}__${destFieldName}`;
                      
                      // Build functionChain - matches etl-frontend structure exactly
                      // Support both old structure (single function) and new structure (functionChain array)
                      let functionChain = [];
                      
                      if (row.functionChain && Array.isArray(row.functionChain) && row.functionChain.length > 0) {
                        // New structure: multi-function chain
                        functionChain = row.functionChain
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((func) => {
                            const payload = {
                              functionName: func.functionName,  // Required - matches etl-frontend
                            };
                            // Add config only if it exists and has values
                            if (func.config && Object.keys(func.config).length > 0) {
                              payload.config = func.config;
                              console.log(`Saving config for function ${func.functionName}:`, func.config);
                            }
                            return payload;
                          });
                      } else if (row.function) {
                        // Old structure: single function (backward compatibility)
                        const payload = {
                          functionName: row.function,  // Only functionName - matches etl-frontend
                        };
                        // Add config only if it exists and has values
                        if (row.functionConfig && Object.keys(row.functionConfig).length > 0) {
                          payload.config = row.functionConfig;
                        }
                        functionChain = [payload];
                      }

                      // Get field types for validation
                      const sourceType = sourceFieldObj.data_type || sourceFieldObj.type || 'string';
                      const destType = destFieldObj.data_type || destFieldObj.type || 'string';

                      // Validate type compatibility
                      const validation = validateMappingType(sourceType, destType, row.function);
                      
                      // Show validation errors if any
                      if (!validation.isValid && validation.errors.length > 0) {
                        const rowNumber = mappingRows.indexOf(row) + 1;
                        validation.errors.forEach((error) => {
                          toast.error(`Row ${rowNumber}: ${error}`, { autoClose: 5000 });
                        });
                        // Continue to next row but don't save invalid mappings
                        continue;
                      }

                      // Create mapping connection object
                      const newMapping = {
                        destinationNodeId: destNode.id,
                        sourceParentId: sourceParentId,
                        sourceFieldId: sourceFieldId,
                        sourceLabel: sourceFieldObj.field_label || sourceFieldObj.label || row.sourceField,
                        type: sourceType,
                        destinationParentId: destinationParentId,
                        destinationFieldId: destinationFieldId,
                        destinationLabel: destFieldObj.field_label || destFieldObj.label || row.destinationField,
                        destinationType: destType,
                        functionChain: functionChain,
                        mappingMeta: {
                          isFuzzy: false,
                          isManual: true,
                          isFunctionEdge: !!row.function,
                          isValid: validation.isValid,
                        },
                      };
                      
                      newMappings.push(newMapping);
                    }
                    
                    // Add/update all new mappings (will replace if key matches)
                    newMappings.forEach((mapping) => {
                      const newKey = keyOf(mapping);
                      mergedMap.set(newKey, mapping);
                    });
                    
                    const updatedMappings = Array.from(mergedMap.values());
                    
                    // Clear editing state if we were editing
                    if (editingMapping) {
                      setEditingMapping(null);
                    }
                    
                    // Update Redux state with all mappings
                    dispatch(
                      setConnectionDetailsForWorkflow({
                        workflowId: effectiveWorkflowId,
                        connectionDetails: updatedMappings,
                      })
                    );
                    
                    // Save all mappings using saveMappingThunk
                    await dispatch(
                      saveMappingThunk({
                        workflow_id: effectiveWorkflowId,
                        mapping_json: {
                          mappings: updatedMappings,
                          filterExpression: null,
                        },
                      })
                    ).unwrap();
                    
                    if (newMappings.length > 0) {
                      toast.success(`${newMappings.length} mapping(s) saved successfully!`, { autoClose: 2000 });
                    } else {
                      toast.warning('No valid mappings to save. Please check for type compatibility errors.', { autoClose: 4000 });
                      return; // Don't clear rows if nothing was saved
                    }
                    
                    // Clear the rows after successful save (keep one empty row)
                    setMappingRows([{ 
                      id: 1, 
                      sourceField: '', 
                      functionType: '', 
                      function: '', 
                      destinationField: '',
                      functionConfig: {}
                    }]);
                    
                    // Refresh saved mappings in preview if dialog is open
                    if (previewOpen) {
                      try {
                        const result = await dispatch(getSavedMapping(effectiveWorkflowId)).unwrap();
                        const mappings = result?.data?.mappings || [];
                        setSavedMappings(mappings);
                      } catch (err) {
                        console.warn('Could not refresh mappings preview:', err);
                      }
                    }
                  } catch (error) {
                    console.error('Failed to save mapping:', error);
                    const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save mapping';
                    
                    // Check if it's a database error
                    if (errorMessage.includes('does not exist') || errorMessage.includes('workflow_mapping')) {
                      toast.error(
                        'Database error: The workflow_mapping table does not exist. Please contact your administrator.',
                        { autoClose: 5000 }
                      );
                    } else {
                      toast.error(`Failed to save mapping: ${errorMessage}`, { autoClose: 4000 });
                    }
                  }
                }}
                  disabled={mappingRows.every(row => !row.sourceField || !row.destinationField)}
                  sx={{ 
                    color: mappingRows.every(row => !row.sourceField || !row.destinationField) ? '#ccc' : '#0056D2',
                    bgcolor: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: '8px',
                    padding: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      color: mappingRows.every(row => !row.sourceField || !row.destinationField) ? '#ccc' : '#0056D2',
                      backgroundColor: mappingRows.every(row => !row.sourceField || !row.destinationField) ? 'transparent' : 'rgba(0, 86, 210, 0.08)',
                      border: mappingRows.every(row => !row.sourceField || !row.destinationField) ? '1px solid transparent' : '1px solid rgba(0, 86, 210, 0.2)',
                      transform: mappingRows.every(row => !row.sourceField || !row.destinationField) ? 'none' : 'translateY(-2px)',
                      boxShadow: mappingRows.every(row => !row.sourceField || !row.destinationField) ? 'none' : '0 4px 8px rgba(0, 86, 210, 0.15)',
                    },
                    '&:active': {
                      transform: mappingRows.every(row => !row.sourceField || !row.destinationField) ? 'none' : 'translateY(0)',
                      boxShadow: mappingRows.every(row => !row.sourceField || !row.destinationField) ? 'none' : '0 2px 4px rgba(0, 86, 210, 0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                      cursor: 'not-allowed',
                      opacity: 0.6,
                    }
                  }}
                >
                  <SaveIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
        </Box>
        );

      default:
        return null;
    }
  };

  // Type validation function - validates if source and destination types are compatible
  const validateMappingType = (sourceType, destinationType, functionId) => {
    let isValid = true;
    const errors = [];

    // Normalize types
    const sourceTypeNormalized = (sourceType || 'unknown').toLowerCase().trim();
    const destTypeNormalized = (destinationType || 'unknown').toLowerCase().trim();

    // Get type aliases for comparison
    const sourceTypeAliases = getAllTypeAliases(sourceTypeNormalized);
    const destTypeAliases = getAllTypeAliases(destTypeNormalized);

    // Check float/double to number mapping (not allowed - loses precision)
    const floatTypes = ['float', 'double'];
    const sourceHasFloat = sourceTypeAliases.some((type) => floatTypes.includes(type));
    const destIsNumber = destTypeAliases.includes('number');

    if (sourceHasFloat && destIsNumber) {
      isValid = false;
      errors.push('Mapping float/double to number may lose decimals and is not allowed.');
      return { isValid, errors };
    }

    // If function is involved, validate function compatibility
    if (functionId) {
      const functionType = functionId;
      const expectedInputTypes = functionTypeCompatibility[functionType] || [];
      const expectedOutputTypes = functionOutputTypes[functionType] || [];

      // Validate source type matches function input requirements
      if (expectedInputTypes.length > 0 && !expectedInputTypes.includes('any')) {
        const sourceMatches = expectedInputTypes.some((type) =>
          sourceTypeAliases.includes(type)
        );
        if (!sourceMatches) {
          isValid = false;
          errors.push(
            `Function "${functionList.find((f) => f.id === functionType)?.label || functionType}" requires input type: ${expectedInputTypes.join(', ')}. Source field type "${sourceTypeNormalized}" is not compatible.`
          );
        }
      }

      // Validate function output matches destination type
      if (expectedOutputTypes.length > 0 && !expectedOutputTypes.includes('any')) {
        const outputMatches = expectedOutputTypes.some((type) =>
          destTypeAliases.includes(type)
        );
        if (!outputMatches) {
          isValid = false;
          errors.push(
            `Function "${functionList.find((f) => f.id === functionType)?.label || functionType}" outputs type: ${expectedOutputTypes.join(', ')}. Destination field type "${destTypeNormalized}" is not compatible.`
          );
        }
      }
    } else {
      // Direct source to destination mapping - check type compatibility
      if (
        sourceTypeNormalized !== 'unknown' &&
        destTypeNormalized !== 'unknown'
      ) {
        const hasTypeIntersection = sourceTypeAliases.some((type) =>
          destTypeAliases.includes(type)
        );
        if (!hasTypeIntersection) {
          isValid = false;
          errors.push(
            `Type mismatch: Source type "${sourceTypeNormalized}" is not compatible with destination type "${destTypeNormalized}".`
          );
        }
      }
    }

    return { isValid, errors };
  };

  // Helper function to extract field name from fieldId
  const getFieldNameFromId = (fieldId) => {
    if (!fieldId) return '';
    if (fieldId.includes('__')) {
      return fieldId.split('__').slice(-1)[0];
    }
    return fieldId;
  };

  // Helper function to check if a function needs configuration
  const functionNeedsConfig = (functionId) => {
    const functionsWithConfig = [
      'substring',
      'field_split',
      'currency_format',
      'remove_special_characters',
      'format_date',
      'add_date',
      'replace',
      'padLeftRight',
      'extract',
      'extract_day',
      'extract_month',
      'extract_year',
      'pad_left',
      'pad_right',
      'join',
      'lookup',
      'relation',
      'registry',
      'aggregation',
    ];
    return functionsWithConfig.includes(functionId);
  };

  // Render function configuration fields based on function type
  const renderFunctionConfig = (row) => {
    const functionId = row.function;
    const config = row.functionConfig || {};

    switch (functionId) {
      case 'substring':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="From (Start Index)"
              type="number"
              size="small"
              fullWidth
              value={config.startFrom || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'startFrom', e.target.value)}
              placeholder="0"
            />
            <TextField
              label="Till (End Index)"
              type="number"
              size="small"
              fullWidth
              value={config.till || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'till', e.target.value)}
              placeholder="5"
            />
          </Box>
        );

      case 'field_split':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Delimiter</InputLabel>
            <Select
              value={config.delimiter || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'delimiter', e.target.value)}
              label="Delimiter"
            >
              <MenuItem value="_">_</MenuItem>
              <MenuItem value="|">|</MenuItem>
              <MenuItem value="~">~</MenuItem>
              <MenuItem value=",">,</MenuItem>
              <MenuItem value="/">/</MenuItem>
              <MenuItem value=":">:</MenuItem>
              <MenuItem value=";">;</MenuItem>
              <MenuItem value="-">-</MenuItem>
              <MenuItem value=".">.</MenuItem>
              <MenuItem value=" ">Space</MenuItem>
            </Select>
          </FormControl>
        );

      case 'currency_format':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Currency Symbol</InputLabel>
              <Select
                value={config.currency_symbol || '₹'}
                onChange={(e) => updateFunctionConfig(row.id, 'currency_symbol', e.target.value)}
                label="Currency Symbol"
              >
                <MenuItem value="₹">₹ (INR)</MenuItem>
                <MenuItem value="$">$ (USD)</MenuItem>
                <MenuItem value="€">€ (EUR)</MenuItem>
                <MenuItem value="£">£ (GBP)</MenuItem>
                <MenuItem value="¥">¥ (JPY)</MenuItem>
                <MenuItem value="₩">₩ (KRW)</MenuItem>
                <MenuItem value="₽">₽ (RUB)</MenuItem>
                <MenuItem value="₺">₺ (TRY)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Decimals"
              type="number"
              size="small"
              fullWidth
              value={config.decimals ?? 2}
              onChange={(e) => updateFunctionConfig(row.id, 'decimals', parseInt(e.target.value) || 2)}
              inputProps={{ min: 0, max: 10 }}
            />
          </Box>
        );

      case 'remove_special_characters':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Select Special Characters</InputLabel>
            <Select
              value={config.characters || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'characters', e.target.value)}
              label="Select Special Characters"
            >
              {['!', '@', '#', '$', '%', '^', '&', '*', '.', ',', ':', ';', '-', '_', '+', '=', '[', ']', '{', '}'].map((char) => (
                <MenuItem key={char} value={char}>{char}</MenuItem>
              ))}
              <MenuItem value="all">Remove All Special Characters</MenuItem>
            </Select>
          </FormControl>
        );

      case 'format_date':
        return (
          <TextField
            label="Date Format"
            size="small"
            fullWidth
            value={config.format || ''}
            onChange={(e) => updateFunctionConfig(row.id, 'format', e.target.value)}
            placeholder="DD/MM/YYYY"
            helperText="Example: DD/MM/YYYY, MM-DD-YYYY, YYYY-MM-DD"
          />
        );

      case 'add_date':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Value"
              type="number"
              size="small"
              fullWidth
              value={config.value || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'value', e.target.value)}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={config.unit || 'days'}
                onChange={(e) => updateFunctionConfig(row.id, 'unit', e.target.value)}
                label="Unit"
              >
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );

      case 'replace':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Find"
              size="small"
              fullWidth
              value={config.find || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'find', e.target.value)}
              placeholder="Text to find"
            />
            <TextField
              label="Replace With"
              size="small"
              fullWidth
              value={config.replace || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'replace', e.target.value)}
              placeholder="Replacement text"
            />
          </Box>
        );

      case 'padLeftRight':
      case 'pad_left':
      case 'pad_right':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Length"
              type="number"
              size="small"
              fullWidth
              value={config.length || ''}
              onChange={(e) => updateFunctionConfig(row.id, 'length', e.target.value)}
            />
            <TextField
              label="Pad Character"
              size="small"
              fullWidth
              value={config.padChar || '0'}
              onChange={(e) => updateFunctionConfig(row.id, 'padChar', e.target.value)}
              placeholder="0"
            />
            {(functionId === 'padLeftRight') && (
              <FormControl size="small" fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={config.direction || 'left'}
                  onChange={(e) => updateFunctionConfig(row.id, 'direction', e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 'extract':
      case 'extract_day':
      case 'extract_month':
      case 'extract_year': {
        const extractValue = functionId === 'extract_day' ? 'day' : 
                            functionId === 'extract_month' ? 'month' : 
                            functionId === 'extract_year' ? 'year' : 
                            config.extract || '';
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Extract</InputLabel>
            <Select
              value={extractValue}
              onChange={(e) => updateFunctionConfig(row.id, 'extract', e.target.value)}
              label="Extract"
              disabled={functionId !== 'extract'}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="year">Year</MenuItem>
            </Select>
          </FormControl>
        );
      }

      case 'aggregation':
        return (
          <TextField
            label="Add a text (optional)"
            size="small"
            fullWidth
            value={config.aggText || ''}
            onChange={(e) => updateFunctionConfig(row.id, 'aggText', e.target.value)}
            placeholder="Optional aggregation text"
          />
        );

      default:
        return (
          <Typography sx={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
            Configuration not available for this function.
          </Typography>
        );
    }
  };

  // Render function configuration for chain functions (similar to renderFunctionConfig but for chain)
  const renderFunctionConfigForChain = (rowId, funcIndex, func) => {
    const functionId = func.functionName;
    const config = func.config || {};

    switch (functionId) {
      case 'substring':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="From (Start Index)"
              type="number"
              size="small"
              fullWidth
              value={config.startFrom || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'startFrom', e.target.value)}
              placeholder="0"
            />
            <TextField
              label="Till (End Index)"
              type="number"
              size="small"
              fullWidth
              value={config.till || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'till', e.target.value)}
              placeholder="5"
            />
          </Box>
        );

      case 'field_split':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Delimiter</InputLabel>
            <Select
              value={config.delimiter || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'delimiter', e.target.value)}
              label="Delimiter"
            >
              <MenuItem value="_">_</MenuItem>
              <MenuItem value="|">|</MenuItem>
              <MenuItem value="~">~</MenuItem>
              <MenuItem value=",">,</MenuItem>
              <MenuItem value="/">/</MenuItem>
              <MenuItem value=":">:</MenuItem>
              <MenuItem value=";">;</MenuItem>
              <MenuItem value="-">-</MenuItem>
              <MenuItem value=".">.</MenuItem>
              <MenuItem value=" ">Space</MenuItem>
            </Select>
          </FormControl>
        );

      case 'currency_format':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Currency Symbol</InputLabel>
              <Select
                value={config.currency_symbol || '₹'}
                onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'currency_symbol', e.target.value)}
                label="Currency Symbol"
              >
                <MenuItem value="₹">₹ (INR)</MenuItem>
                <MenuItem value="$">$ (USD)</MenuItem>
                <MenuItem value="€">€ (EUR)</MenuItem>
                <MenuItem value="£">£ (GBP)</MenuItem>
                <MenuItem value="¥">¥ (JPY)</MenuItem>
                <MenuItem value="₩">₩ (KRW)</MenuItem>
                <MenuItem value="₽">₽ (RUB)</MenuItem>
                <MenuItem value="₺">₺ (TRY)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Decimals"
              type="number"
              size="small"
              fullWidth
              value={config.decimals ?? 2}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'decimals', parseInt(e.target.value) || 2)}
              inputProps={{ min: 0, max: 10 }}
            />
          </Box>
        );

      case 'format_date':
        return (
          <TextField
            label="Date Format"
            size="small"
            fullWidth
            value={config.format || ''}
            onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'format', e.target.value)}
            placeholder="e.g., DD/MM/YYYY"
          />
        );

      case 'add_date':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Days"
              type="number"
              size="small"
              fullWidth
              value={config.days || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'days', e.target.value)}
              placeholder="0"
            />
            <TextField
              label="Months"
              type="number"
              size="small"
              fullWidth
              value={config.months || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'months', e.target.value)}
              placeholder="0"
            />
            <TextField
              label="Years"
              type="number"
              size="small"
              fullWidth
              value={config.years || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'years', e.target.value)}
              placeholder="0"
            />
          </Box>
        );

      case 'replace':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Find"
              size="small"
              fullWidth
              value={config.find || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'find', e.target.value)}
              placeholder="Text to find"
            />
            <TextField
              label="Replace With"
              size="small"
              fullWidth
              value={config.replace || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'replace', e.target.value)}
              placeholder="Replacement text"
            />
          </Box>
        );

      case 'padLeftRight':
      case 'pad_left':
      case 'pad_right':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Length"
              type="number"
              size="small"
              fullWidth
              value={config.length || ''}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'length', e.target.value)}
              placeholder="10"
            />
            <TextField
              label="Pad Character"
              size="small"
              fullWidth
              value={config.padChar || '0'}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'padChar', e.target.value)}
              placeholder="0"
            />
            {(functionId === 'padLeftRight') && (
              <FormControl size="small" fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={config.direction || 'left'}
                  onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'direction', e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 'extract':
      case 'extract_day':
      case 'extract_month':
      case 'extract_year': {
        const extractValue = functionId === 'extract_day' ? 'day' : 
                            functionId === 'extract_month' ? 'month' : 
                            functionId === 'extract_year' ? 'year' : 
                            config.extract || '';
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Extract</InputLabel>
            <Select
              value={extractValue}
              onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'extract', e.target.value)}
              label="Extract"
              disabled={functionId !== 'extract'}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="year">Year</MenuItem>
            </Select>
          </FormControl>
        );
      }

      case 'aggregation':
        return (
          <TextField
            label="Add a text (optional)"
            size="small"
            fullWidth
            value={config.aggText || ''}
            onChange={(e) => updateFunctionConfigInChain(rowId, funcIndex, 'aggText', e.target.value)}
            placeholder="Optional aggregation text"
          />
        );

      default:
        return (
          <Typography sx={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
            Configuration not available for this function.
          </Typography>
        );
    }
  };

  // Helper functions for managing mapping rows
  const addMappingRow = () => {
    const newId = Math.max(...mappingRows.map(r => r.id), 0) + 1;
    setMappingRows([...mappingRows, { 
      id: newId, 
      sourceField: '', 
      functionType: '',      // For backward compatibility
      function: '',           // For backward compatibility
      destinationField: '',
      functionConfig: {},     // For backward compatibility
      functionChain: []       // New: array for multi-function chains
    }]);
  };

  const removeMappingRow = (rowId) => {
    if (mappingRows.length > 1) {
      setMappingRows(mappingRows.filter(row => row.id !== rowId));
    }
  };

  const updateMappingRow = (rowId, field, value) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id === rowId) {
        const updated = { ...row, [field]: value };
        // If functionType changes, clear function and config
        if (field === 'functionType' && value === '') {
          updated.function = '';
          updated.functionConfig = {};
          updated.showFunctionConfig = undefined; // Reset config panel state
        }
        // If function changes, show config panel if function needs config
        if (field === 'function' && value) {
          if (!functionNeedsConfig(value)) {
            updated.functionConfig = {};
            updated.showFunctionConfig = undefined;
          } else {
            // Show config panel when function that needs config is selected
            if (!updated.functionConfig) {
              updated.functionConfig = {};
            }
            // Show config panel by default when function is selected
            updated.showFunctionConfig = true;
          }
        }
        return updated;
      }
      return row;
    }));
  };

  // Function chain management functions
  const addFunctionToChain = (rowId) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const existingChain = row.functionChain || [];
      // Calculate the next order number based on existing functions
      const maxOrder = existingChain.length > 0 
        ? Math.max(...existingChain.map(f => f.order || 0))
        : -1;
      const nextOrder = maxOrder + 1;
      
      const newFunction = {
        id: `func-${Date.now()}-${Math.random()}`,
        functionName: '',
        config: {},
        order: nextOrder,
        showConfig: false,
        functionType: '' // Add functionType to track category selection
      };
      
      return {
        ...row,
        functionChain: [...existingChain, newFunction]
      };
    }));
  };

  const removeFunctionFromChain = (rowId, funcIndex) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const chain = [...(row.functionChain || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      const removedChain = chain.filter((_, idx) => idx !== funcIndex);
      
      // Reassign orders sequentially starting from 0
      const updatedChain = removedChain.map((func, idx) => ({ 
        ...func, 
        order: idx 
      }));
      
      return {
        ...row,
        functionChain: updatedChain
      };
    }));
  };

  const moveFunctionInChain = (rowId, funcIndex, direction) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const chain = [...(row.functionChain || [])];
      const newIndex = direction === 'up' ? funcIndex - 1 : funcIndex + 1;
      
      if (newIndex < 0 || newIndex >= chain.length) return row;
      
      // Swap functions
      [chain[funcIndex], chain[newIndex]] = [chain[newIndex], chain[funcIndex]];
      
      // Update orders
      chain.forEach((func, idx) => {
        func.order = idx;
      });
      
      return {
        ...row,
        functionChain: chain
      };
    }));
  };

  const updateFunctionInChain = (rowId, funcIndex, updatedFunc) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const updatedChain = [...(row.functionChain || [])];
      updatedChain[funcIndex] = { ...updatedFunc };
      
      return {
        ...row,
        functionChain: updatedChain
      };
    }));
  };

  const toggleFunctionConfig = (rowId, funcIndex) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const updatedChain = [...(row.functionChain || [])];
      updatedChain[funcIndex] = {
        ...updatedChain[funcIndex],
        showConfig: !updatedChain[funcIndex].showConfig
      };
      
      return {
        ...row,
        functionChain: updatedChain
      };
    }));
  };

  // Update function config for a specific row (backward compatibility - single function)
  const updateFunctionConfig = (rowId, configKey, configValue) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          functionConfig: {
            ...(row.functionConfig || {}),
            [configKey]: configValue
          }
        };
      }
      return row;
    }));
  };

  // Update function config in chain
  const updateFunctionConfigInChain = (rowId, funcIndex, configKey, configValue) => {
    setMappingRows(mappingRows.map(row => {
      if (row.id !== rowId) return row;
      
      const updatedChain = [...(row.functionChain || [])];
      const updatedConfig = {
        ...(updatedChain[funcIndex].config || {}),
        [configKey]: configValue
      };
      
      updatedChain[funcIndex] = {
        ...updatedChain[funcIndex],
        config: updatedConfig
      };
      
      console.log(`Updated config for function ${updatedChain[funcIndex].functionName}:`, updatedConfig);
      
      return {
        ...row,
        functionChain: updatedChain
      };
    }));
  };

  // Handle edit mapping - populate rows with the mapping data
  const handleEditMapping = (mapping) => {
    // Extract field names from IDs
    const sourceFieldName = getFieldNameFromId(mapping.sourceFieldId);
    const destFieldName = getFieldNameFromId(mapping.destinationFieldId);
    
    // Convert functionChain to new structure (supports both old and new)
    let functionChain = [];
    let functionType = '';
    let functionId = '';
    let functionConfig = {};
    
    if (mapping.functionChain && Array.isArray(mapping.functionChain) && mapping.functionChain.length > 0) {
      // New structure: multi-function chain
      functionChain = mapping.functionChain.map((func, index) => ({
        id: `func-${Date.now()}-${index}`,
        functionName: func.functionName || func.functionType || '',
        config: func.config || {},
        order: index,
        showConfig: false
      }));
      
      // For backward compatibility, also set single function fields
      const firstFunc = mapping.functionChain[0];
      functionId = firstFunc.functionName || firstFunc.functionType || '';
      functionConfig = firstFunc.config || {};
      
      // Find which category this function belongs to
      const category = Object.keys(groupedFunctions).find((cat) =>
        groupedFunctions[cat].includes(functionId)
      );
      if (category) {
        functionType = category;
      }
    }

    // Set the first row with the mapping data
    setMappingRows([{
      id: 1,
      sourceField: sourceFieldName,
      functionType: functionType,        // For backward compatibility
      function: functionId,                // For backward compatibility
      destinationField: destFieldName,
      functionConfig: functionConfig,     // For backward compatibility
      functionChain: functionChain        // New: multi-function chain
    }]);
    
    // Store the mapping being edited
    setEditingMapping(mapping);
    setPreviewOpen(false);
    toast.info('Mapping loaded for editing. Update and save to apply changes.');
  };

  // Handle discard all mappings
  const handleConfirmDiscard = async () => {
    try {
      // Save empty mappings to backend to clear server data
      await dispatch(
        saveMappingThunk({
          workflow_id: effectiveWorkflowId,
          mapping_json: {
            mappings: [],
            filterExpression: null,
          },
        })
      ).unwrap();

      // Reset mapping rows to initial state
      setMappingRows([{
        id: 1,
        sourceField: '',
        functionType: '',
        function: '',
        destinationField: '',
        functionConfig: {},
        functionChain: []
      }]);
      
      // Clear Redux state
      dispatch(setConnectionDetailsForWorkflow({
        workflowId: effectiveWorkflowId,
        connectionDetails: []
      }));
      
      // Clear fuzzy state
      dispatch(clearFuzzyStateForWorkflow({ workflowId: effectiveWorkflowId }));
      
      // Reset other state
      setSavedMappings([]);
      setEditingMapping(null);
      
      // Close preview dialog if open
      setPreviewOpen(false);
      
      // Close dialog
      setDiscardConfirmOpen(false);
      
      toast.success('All mappings discarded.', { autoClose: 2000 });
    } catch (error) {
      console.error('Failed to discard mappings:', error);
      toast.error('Failed to discard mappings. Please try again.');
    }
  };

  // Handle delete mapping - opens confirmation dialog
  const handleDeleteMapping = (mapping) => {
    setPendingDeleteMapping(mapping);
    setDeleteConfirmOpen(true);
  };

  // Handle confirm delete mapping
  const handleConfirmDeleteMapping = async () => {
    console.log('BottomConfigurationPanel: handleConfirmDeleteMapping called');
    console.log('BottomConfigurationPanel: pendingDeleteMapping:', pendingDeleteMapping);
    if (!pendingDeleteMapping) {
      console.warn('BottomConfigurationPanel: No pending mapping to delete');
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      const state = store.getState();
      const existingMappings = state?.mapping?.mappingsByWorkflow?.[effectiveWorkflowId]?.connectionDetails || [];
      
      // Create key function to identify the mapping to delete
      // Include destinationNodeId to match the save mapping key format
      const keyOf = (m) => {
        const destNodeId = m.destinationNodeId || '';
        
        const getEntityName = (parentId) => {
          if (!parentId) return '';
          if (parentId.includes('__')) {
            return parentId.split('__').slice(-1)[0];
          }
          return parentId;
        };
        
        const getFieldName = (fieldId) => {
          if (!fieldId) return '';
          if (fieldId.includes('__')) {
            return fieldId.split('__').slice(-1)[0];
          }
          return fieldId;
        };
        
        const destEntity = getEntityName(m.destinationParentId);
        const destField = getFieldName(m.destinationFieldId);
        const srcEntity = getEntityName(m.sourceParentId);
        const srcField = getFieldName(m.sourceFieldId);
        
        // Include destinationNodeId to match save mapping key format
        return `${destNodeId}::${destEntity}::${destField}::${srcEntity}::${srcField}`;
      };

      const mappingKey = keyOf(pendingDeleteMapping);
      const updatedMappings = existingMappings.filter((m) => keyOf(m) !== mappingKey);

      // Update Redux state
      dispatch(
        setConnectionDetailsForWorkflow({
          workflowId: effectiveWorkflowId,
          connectionDetails: updatedMappings,
        })
      );

      // Save to backend
      await dispatch(
        saveMappingThunk({
          workflow_id: effectiveWorkflowId,
          mapping_json: {
            mappings: updatedMappings,
            filterExpression: null,
          },
        })
      ).unwrap();

      // Update preview if open
      setSavedMappings(updatedMappings);
      toast.success('Mapping deleted successfully!', { autoClose: 2000 });
      
      // Close dialog and clear pending mapping
      setDeleteConfirmOpen(false);
      setPendingDeleteMapping(null);
    } catch (error) {
      console.error('Failed to delete mapping:', error);
      toast.error('Failed to delete mapping');
      setDeleteConfirmOpen(false);
      setPendingDeleteMapping(null);
    }
  };

  if (!showMappingPanel && currentStep !== 'mapping') {
    return null; // Don't show panel unless mapping step or explicitly shown
  }

  return (
    <Box
      sx={{
        width: isFullscreen ? '100vw' : { xs: '100%', md: 'calc(100% - 265px)' },
        height: isFullscreen ? '100vh' : 'auto',
        bgcolor: '#f5f5f5',
        borderTop: isFullscreen ? 'none' : '1px solid #dcdcdc',
        position: isFullscreen ? 'fixed' : 'sticky',
        bottom: 0,
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 5,
        boxShadow: isFullscreen ? 'none' : '0 -2px 8px rgba(0,0,0,0.08)',
        ml: isFullscreen ? 0 : { xs: 0, md: '265px' },
        maxHeight: isFullscreen ? '100vh' : (collapsed ? '60px' : '500px'),
        transition: 'max-height 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out',
        overflow: collapsed ? 'hidden' : (isFullscreen ? 'visible' : 'hidden'),
        overflowY: collapsed ? 'hidden' : (isFullscreen ? 'auto' : 'hidden'),
        overflowX: 'visible',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          bgcolor: '#525252',
          color: '#FFFFFF',
          px: 1.5,
          py: 0.25,
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontWeight: 500,
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box sx={{ 
            mb: 0,
            '& > .MuiBox-root': {
              marginBottom: '0 !important',
            },
            '& .MuiTab-root': {
              color: '#FFFFFF',
              opacity: 0.7,
              padding: '3px 12px',
              fontSize: '0.85rem',
              '&.Mui-selected': {
                color: '#FFFFFF',
                opacity: 1,
                borderBottom: '3px solid #FFFFFF',
              },
            },
            '& .MuiTabs-root': {
              marginBottom: 0,
            },
            '& .MuiTabs-scroller': {
              marginBottom: 0,
            },
          }}>
            <PrimaryTabs
              activeTab={bottomPanelTab}
              onTabChange={setBottomPanelTab}
              tabs={[
                { label: 'Configuration' },
                { label: 'Execution' },
              ]}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} arrow>
            <IconButton
              size="small"
              onClick={() => {
                setIsFullscreen(!isFullscreen);
                if (!isFullscreen) {
                  setCollapsed(false); // Auto-expand when entering fullscreen
                }
              }}
              sx={{ color: '#FFFFFF' }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon sx={{ fontSize: 18 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          {!isFullscreen && (
            <Tooltip title={collapsed ? 'Expand' : 'Collapse'} arrow>
              <IconButton
                size="small"
                onClick={() => setCollapsed(!collapsed)}
                sx={{ color: '#FFFFFF' }}
              >
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {!collapsed && (
        <>
          {bottomPanelTab === 0 && (
            <>
              {renderProgressIndicator()}
              <Box
                ref={configScrollRef}
                sx={{
                  bgcolor: '#fff',
                  overflowY: 'auto',
                  overflowX: 'visible',
                  flex: 1,
                  position: 'relative',
                  scrollBehavior: 'smooth',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isScrolling ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0)',
                    borderRadius: '4px',
                    transition: 'background 0.3s ease, opacity 0.3s ease',
                    opacity: isScrolling ? 1 : 0,
                  },
                  '&:hover::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.4)',
                    opacity: 1,
                  },
                  // Firefox scrollbar
                  scrollbarWidth: 'thin',
                  scrollbarColor: isScrolling ? 'rgba(0, 0, 0, 0.4) transparent' : 'rgba(0, 0, 0, 0) transparent',
                }}
              >
                  {renderStepContent(currentStep)}
              </Box>
            </>
          )}
          <Box
            sx={{
              bgcolor: '#fff',
              overflowY: 'hidden',
              overflowX: 'visible',
              flex: 1,
              position: 'relative',
              width: '100%',
              height: '100%',
              display: bottomPanelTab === 1 ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <ActionButtons
              ref={actionRef}
              selectedNode={selectedNode}
              workflowId={workflowId}
              workflowConfigurationId={workflowConfigurationId}
              onWorkflowExecutionComplete={onWorkflowExecutionComplete}
              onSave={onSave}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={onCollapsedChange}
              leftSidebarRef={leftSidebarRef}
              rightSidebarRef={rightSidebarRef}
              onExecutionStart={() => {
                console.log('Switching to Execution tab');
                setBottomPanelTab(1);
              }}
              fullWidth={true}
            />
          </Box>
        </>
      )}

      {/* Mapping Preview Dialog */}
      <PrimaryDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Mapping Preview"
        subtitle={`View and manage all saved mappings (${savedMappings.length} total)`}
        maxWidth="lg"
        submitButtonText="Close"
        onSave={() => setPreviewOpen(false)}
      >
        <Box sx={{ mt: 2 }}>
          {savedMappings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                No mappings saved yet. Create mappings using the form above.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Source Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Function</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Destination Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savedMappings.map((mapping, idx) => {
                    const sourceField = getFieldNameFromId(mapping.sourceFieldId) || mapping.sourceLabel || 'N/A';
                    const destField = getFieldNameFromId(mapping.destinationFieldId) || mapping.destinationLabel || 'N/A';
                    const functionName = mapping.functionChain && mapping.functionChain.length > 0
                      ? (functionList.find((f) => f.id === (mapping.functionChain[0].functionType || mapping.functionChain[0].functionName))?.label || mapping.functionChain[0].functionName || 'N/A')
                      : 'None';
                    
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>{sourceField}</TableCell>
                        <TableCell>
                          <Chip
                            label={functionName}
                            size="small"
                            sx={{
                              bgcolor: functionName !== 'None' ? '#e3f2fd' : '#f5f5f5',
                              color: functionName !== 'None' ? '#1976d2' : '#666',
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>{destField}</TableCell>
                        <TableCell>
                          <Chip
                            label={mapping.mappingMeta?.isFuzzy ? 'Auto' : 'Manual'}
                            size="small"
                            sx={{
                              bgcolor: mapping.mappingMeta?.isFuzzy ? '#e8f5e9' : '#fff3e0',
                              color: mapping.mappingMeta?.isFuzzy ? '#2e7d32' : '#e65100',
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditMapping(mapping)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMapping(mapping)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </PrimaryDialog>

      {/* Discard Mapping Confirmation Dialog */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard All Mappings?"
        content="Are you sure you want to discard all mappings? This will remove all mapping rows and clear all saved mappings."
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      {/* Delete Mapping Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Mapping?"
        content="Are you sure you want to delete this mapping? This action cannot be undone."
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteMapping(null);
        }}
        onConfirm={handleConfirmDeleteMapping}
        zIndex={1400}
      />
    </Box>
  );
};

BottomConfigurationPanel.propTypes = {
  currentStep: PropTypes.oneOf(['selectDataset', 'configureDataset', 'entitySelection', 'mapping']),
  onStepChange: PropTypes.func,
  mappingData: PropTypes.object,
  resolvedFields: PropTypes.array,
  autoMappedFields: PropTypes.object,
  selectedNode: PropTypes.object,
  showMappingPanel: PropTypes.bool,
  nodes: PropTypes.array,
  workflowId: PropTypes.string,
  onEntitySelectionComplete: PropTypes.func,
  onSwitchToVisualBuilder: PropTypes.func,
  actionRef: PropTypes.object,
  workflowConfigurationId: PropTypes.string,
  onWorkflowExecutionComplete: PropTypes.func,
  onSave: PropTypes.func,
  collapsedBoth: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  leftSidebarRef: PropTypes.any,
  rightSidebarRef: PropTypes.any,
  updateNodeData: PropTypes.func,
  onNodeSelect: PropTypes.func,
};

export default BottomConfigurationPanel;
